import { useState, useEffect, useLayoutEffect, useRef, useMemo, useCallback, lazy, Suspense } from "react";
import Header from "@/components/Header";
import type { EventDrawerEvent } from "@/components/EventDrawer";
import type { DateGroup } from "@/components/home/EventGroupsSection";
import { HomeLandingIntro } from "@/components/home/HomeLandingIntro";
import { EventsDateBar } from "@/components/home/EventsDateBar";
import { ExploreModeTabs } from "@/components/home/ExploreModeTabs";
import {
  UnifiedDiscoveryBar,
  applyQuickDate,
  getActiveQuickDate,
  type DiscoveryFilterChip,
  type QuickDate,
} from "@/components/home/UnifiedDiscoveryBar";
import { GuideShortcuts } from "@/components/home/GuideShortcuts";
import { GuidePicksRow, selectGuidePicks } from "@/components/home/GuidePicksRow";
import { buildGuidePickMap } from "@/lib/guidePicks";
import { compareByStartTime, parseTimeToMinutes, splitByDayPeriod, type DayPeriod } from "@/lib/dayPeriods";
import { DayPeriodJumpBar, DayPeriodJumpSpacer } from "@/components/home/DayPeriods";
import { ListEndNote } from "@/components/home/ListEndNote";
import { getFilterUrlBase } from "@/lib/filterUrl";
import { headerHoldGeneration, isHeaderHeld } from "@/lib/headerScrollHold";
import type { GuideSummary } from "@/components/home/GuidePanel";
import type { GuideLinkedEvent } from "@shared/guideEvents";
const CompactEventItem = lazy(() => import("@/components/CompactEventItem"));
const EventGroupsSection = lazy(() =>
  import("@/components/home/EventGroupsSection").then((m) => ({ default: m.EventGroupsSection }))
);
const FeaturedEventsSection = lazy(() =>
  import("@/components/home/FeaturedEventsSection").then((m) => ({ default: m.FeaturedEventsSection }))
);
const GuidePanel = lazy(() =>
  import("@/components/home/GuidePanel").then((m) => ({ default: m.GuidePanel }))
);
const EventDrawer = lazy(() => import("@/components/EventDrawer"));
const OnboardingToast = lazy(() => import("@/components/OnboardingToast").then((m) => ({ default: m.OnboardingToast })));
const SourceCardsGrid = lazy(() => import("@/components/SourceCardsGrid").then(m => ({ default: m.SourceCardsGrid })));
import { type TimePeriod, filterEventsByTimePeriods } from "@/components/DateFilterPills";
const SurpriseEventCard = lazy(() => import("@/components/SurpriseEventCard"));
import Footer from "@/components/Footer";
const FeaturedCarousel = lazy(() => import("@/components/FeaturedCarousel").then(m => ({ default: m.FeaturedCarousel })));
import { useQuery, useInfiniteQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { type Event, type MasterCategory } from "@shared/schema";
import { X, ChevronUp, ChevronDown, SlidersHorizontal, EyeOff, Search, Calendar, Clock, Info, Bookmark, Trash2, BookOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAppUser";
import { useBootstrapData } from "@/lib/bootstrap";
import { cn } from "@/lib/utils";
import { Link, useLocation, useParams } from "wouter";
import { Helmet } from "react-helmet-async";
import { pickRandomEvent, filterEventsWithin7Days } from "@/lib/randomEventUtils";
import type { DateFilterType } from "@/components/FiltersPanel";
import type { UnifiedFilterCountsResponse } from "@/hooks/useUnifiedFilterCounts";
import FiltersDialog, { type DialogFilterDraft } from "@/components/FiltersDialog";
import { getCategoryTagStyles } from "@/lib/categories";
import { useAutoScroll } from "@/hooks/useAutoScroll";
import { reportApiError, trackEvent } from "@/lib/analytics";
import { applyListScroll, currentListUrl, measureListScroll, readListScroll, saveListScroll, wasBackForwardNavigation, type ListScrollPosition } from "@/lib/scrollMemory";
import { trackSearch, trackViewSwitch, getSessionId, trackFeaturedInteraction, trackFilterUiInteraction, trackGuideButtonInteraction, type FeaturedInteractionType, type SearchFilters } from "@/lib/trackSearch";
import NotFound from "@/pages/not-found";
import { getRegionPathFromName, slugify } from "@shared/routeSlugs";
import {
  buildBreadcrumbSchema,
  HOME_BASE_URL,
  HOME_PAGE_DESCRIPTION,
  HOME_PAGE_TITLE,
  HOMEPAGE_H1,
  HOMEPAGE_INTRO,
  HOMEPAGE_REGIONS,
  ORGANIZATION_SCHEMA,
  SHARE_IMAGE_URL,
  WEBSITE_SCHEMA,
  getCategoryPathFromName,
  getCategorySeoConfigByName,
  getCategorySeoConfigBySlug,
  buildRegionDateIntentMeta,
  getDateIntentSeoConfigByKey,
  getRegionDateIntentPath,
  getRegionPageDescription,
  getRegionPageTitle,
  isNonTownRegion,
  type DateIntentKey,
} from "@shared/seo";
const FiltersPanel = lazy(() => import("@/components/FiltersPanel"));

type ViewMode = 'HOME' | 'SURPRISE';
type FilterUiState = 'closed' | 'mobile' | 'desktop';
type ExploreMode = 'events' | 'sources' | 'recently-viewed';

const EXPLORE_MODE_KEY = 'larks-explore-mode';
const FILTER_STORAGE_KEY = 'oal_saved_filters';
const FILTER_STORAGE_EXPIRY_DAYS = 60;
const HERO_COLLAPSED_STORAGE_KEY = 'oal_discovery_hero_collapsed';
const PENDING_MOBILE_BROWSE_KEY = 'oal_pending_mobile_browse';
const HERO_STICKY_TOP = 'var(--header-offset, var(--header-height, 92px))';

type PendingMobileBrowseSelection = {
  kind: 'region' | 'category';
  label: string;
  path: string;
};

function clearPendingMobileBrowseSelection() {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(PENDING_MOBILE_BROWSE_KEY);
  } catch {
    // Storage can be unavailable in privacy-restricted browsing contexts.
  }
}

/** Brings the start of the event results just under the header and moves focus there */
function scrollToEventResults() {
  const results = document.getElementById('event-results');
  if (!results) return;

  const rootStyles = window.getComputedStyle(document.documentElement);
  const headerOffset = Number.parseFloat(rootStyles.getPropertyValue('--header-offset')) || 92;
  const targetTop = results.getBoundingClientRect().top + window.scrollY - headerOffset - 12;
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  window.scrollTo({
    top: Math.max(0, targetTop),
    behavior: prefersReducedMotion ? 'auto' : 'smooth',
  });
  results.focus({ preventScroll: true });
}

interface SavedFilters {
  version: 1;
  savedAt: string;
  selectedCategories: string[];
  excludedCategories: string[];
  selectedSources: string[];
  excludedSources: string[];
  selectedRegions: string[];
  excludedRegions: string[];
  dateFilter: DateFilterType;
  customDate: string | null;
  timePeriods: TimePeriod[];
}

function readPendingMobileBrowseSelection(): PendingMobileBrowseSelection | null {
  if (typeof window === 'undefined') return null;

  try {
    const saved = window.sessionStorage.getItem(PENDING_MOBILE_BROWSE_KEY);
    if (!saved) return null;
    const parsed = JSON.parse(saved) as Partial<PendingMobileBrowseSelection>;
    if (
      (parsed.kind === 'region' || parsed.kind === 'category') &&
      typeof parsed.label === 'string' &&
      typeof parsed.path === 'string'
    ) {
      return parsed as PendingMobileBrowseSelection;
    }
  } catch {
    clearPendingMobileBrowseSelection();
  }

  return null;
}

function urlHasFilterParams(): boolean {
  const params = new URLSearchParams(window.location.search);
  return ['categories', 'excludedCategories', 'sources', 'excludedSources', 'regions', 'excludedRegions', 'date', 'time', 'selectedSource']
    .some(key => params.has(key));
}

function toLocalDateString(date: Date): string {
  return date.toLocaleDateString('en-CA');
}

function extractDateString(eventDate: string | Date): string {
  if (typeof eventDate === 'string') {
    // Check if already in YYYY-MM-DD format (ISO)
    if (eventDate.includes('T')) {
      return eventDate.split('T')[0];
    }
    // Handle MM/DD/YYYY format from database (may have extra text after date)
    if (eventDate.includes('/')) {
      const parts = eventDate.split('/');
      if (parts.length >= 3) {
        const month = parts[0].replace(/\D/g, '').padStart(2, '0');
        const day = parts[1].replace(/\D/g, '').padStart(2, '0');
        // Extract only the numeric year (first 4 digits) - handles "2025 ~ approximate..." 
        const yearMatch = parts[2].match(/\d{4}/);
        const year = yearMatch ? yearMatch[0] : parts[2].replace(/\D/g, '').slice(0, 4);
        if (month && day && year && year.length === 4) {
          return `${year}-${month}-${day}`;
        }
      }
    }
    // Check if it looks like YYYY-MM-DD at the start
    const isoMatch = eventDate.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (isoMatch) {
      return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;
    }
    // Return as-is for other formats
    return eventDate;
  }
  return eventDate.toISOString().split('T')[0];
}

function isValidDateString(dateString: string): boolean {
  const parts = dateString.split('-');
  if (parts.length !== 3) return false;
  const [year, month, day] = parts.map(Number);
  if (isNaN(year) || isNaN(month) || isNaN(day)) return false;
  if (month < 1 || month > 12 || day < 1 || day > 31) return false;
  return true;
}

function groupEventsByDate(events: Event[]): DateGroup[] {
  const groups = new Map<string, Event[]>();
  
  events.forEach(event => {
    const dateStr = extractDateString(event.date);
    if (!groups.has(dateStr)) {
      groups.set(dateStr, []);
    }
    groups.get(dateStr)!.push(event);
  });
  
  return Array.from(groups.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([dateString, groupEvents]) => ({
      dateString,
      // By start time, events with no set time last: the order the server pages them in,
      // so a newly loaded page only adds events below the ones already on screen
      events: [...groupEvents].sort(compareByStartTime),
    }));
}

function getDateFilterDates(filter: DateFilterType, customDate: string | null): string[] {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  switch (filter) {
    case 'today':
      return [toLocalDateString(today)];
    case 'tomorrow': {
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      return [toLocalDateString(tomorrow)];
    }
    case 'weekend': {
      const dayOfWeek = today.getDay();
      let saturday: Date;
      let sunday: Date;
      
      if (dayOfWeek === 0) {
        sunday = new Date(today);
        saturday = new Date(today);
        saturday.setDate(saturday.getDate() - 1);
      } else if (dayOfWeek === 6) {
        saturday = new Date(today);
        sunday = new Date(today);
        sunday.setDate(sunday.getDate() + 1);
      } else {
        const daysUntilSaturday = 6 - dayOfWeek;
        saturday = new Date(today);
        saturday.setDate(today.getDate() + daysUntilSaturday);
        sunday = new Date(saturday);
        sunday.setDate(saturday.getDate() + 1);
      }
      
      const friday = new Date(saturday);
      friday.setDate(saturday.getDate() - 1);
      
      return [
        toLocalDateString(friday),
        toLocalDateString(saturday),
        toLocalDateString(sunday)
      ];
    }
    case 'custom':
      return customDate ? [customDate] : [];
    default:
      return [];
  }
}

function DrawerShell() {
  return (
    <div className="flex h-full flex-col bg-[rgba(255,255,248,0.998)]">
      <div className="h-[132px] animate-pulse border-b border-[#e5dfd1] bg-[linear-gradient(180deg,rgba(255,251,236,0.96),rgba(255,255,248,0.98))]" />
      <div className="space-y-4 p-4">
        <div className="h-14 animate-pulse rounded-2xl bg-muted/60" />
        <div className="h-12 animate-pulse rounded-2xl bg-muted/50" />
        <div className="h-12 animate-pulse rounded-2xl bg-muted/45" />
      </div>
    </div>
  );
}

function EventCardGridShell({ count = 6 }: { count?: number }) {
  return (
    <div className="mobile-event-card-list grid gap-4 py-4" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(360px, 1fr))" }}>
      {Array.from({ length: count }, (_, index) => (
        <div
          key={index}
          className="min-h-[280px] rounded-xl border border-border/80 bg-card/70 animate-pulse"
        />
      ))}
    </div>
  );
}

function EventGroupsShell() {
  return <EventCardGridShell count={8} />;
}

interface QuickPicksSectionProps {
  guidePanel: "week" | "weekend" | null;
  onGuideButton: (type: "week" | "weekend") => void;
  weekTestId: string;
  weekendTestId: string;
  className?: string;
  buttonsClassName?: string;
  headingClassName?: string;
}

function QuickPicksSection({
  guidePanel,
  onGuideButton,
  weekTestId,
  weekendTestId,
  className,
  buttonsClassName,
  headingClassName,
}: QuickPicksSectionProps) {
  const [isIntroVisible, setIsIntroVisible] = useState(false);
  const quickPickButtonClassName =
    "inline-flex min-h-[44px] shrink-0 items-center justify-center gap-1.5 rounded-full border px-3 py-2 text-[12px] font-semibold leading-none transition-colors hover:bg-[#edf5ee] hover:border-[#bfd4c4] active:bg-[#e3efe6] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1D9E75] focus-visible:ring-offset-2 sm:w-auto sm:flex-1 sm:rounded-[14px] sm:px-3.5 sm:text-[12px]";

  useEffect(() => {
    if (typeof window === "undefined") {
      setIsIntroVisible(true);
      return;
    }

    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReducedMotion) {
      setIsIntroVisible(true);
      return;
    }

    const frame = window.requestAnimationFrame(() => {
      setIsIntroVisible(true);
    });

    return () => window.cancelAnimationFrame(frame);
  }, []);

  return (
    <section
      aria-labelledby="quick-picks-heading"
      className={cn("flex items-center gap-2 transition-[opacity,transform] duration-300 ease-out motion-reduce:transform-none motion-reduce:transition-none sm:block sm:space-y-2", className)}
      style={{
        opacity: isIntroVisible ? 1 : 0.72,
        transform: isIntroVisible ? "translateY(0)" : "translateY(6px)",
      }}
      data-testid="quick-picks-section"
    >
      <h2
        id="quick-picks-heading"
        className={cn(
          "shrink-0 text-[8px] font-semibold uppercase tracking-[0.06em] text-muted-foreground sm:text-[9px] sm:tracking-[0.04em]",
          headingClassName,
        )}
      >
        Quick Picks
      </h2>
      <div
        className={cn("flex min-w-0 flex-1 items-center gap-2 overflow-x-auto whitespace-nowrap sm:flex-row sm:flex-wrap sm:gap-2 sm:overflow-visible sm:whitespace-normal", buttonsClassName)}
        style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
        role="group"
        aria-label="Quick picks"
      >
        <button
          type="button"
          onClick={() => onGuideButton("week")}
          className={quickPickButtonClassName}
          style={guidePanel === "week"
            ? {
                border: "1.5px solid #9fc9ab",
                backgroundColor: "#e7f3e8",
                color: "#23483b",
                boxShadow: "var(--brutal-shadow-sm)",
              }
            : {
                border: "1.5px solid #d7e4d9",
                backgroundColor: "#f4f8f3",
                color: "#335547",
              }}
          data-testid={weekTestId}
          aria-pressed={guidePanel === "week"}
          aria-label={guidePanel === "week" ? "Close this week's guide" : "Open this week's guide"}
        >
          {guidePanel === "week" ? (
            <X className="h-3.5 w-3.5 shrink-0" />
          ) : (
            <BookOpen className="h-3.5 w-3.5 shrink-0" />
          )}
          What's On This Week
          {guidePanel === "week" && (
            <span className="hidden text-[10px] font-semibold uppercase tracking-[0.08em] sm:inline">
              Close
            </span>
          )}
        </button>
        <button
          type="button"
          onClick={() => onGuideButton("weekend")}
          className={quickPickButtonClassName}
          style={guidePanel === "weekend"
            ? {
                border: "1.5px solid #9fc9ab",
                backgroundColor: "#e7f3e8",
                color: "#23483b",
                boxShadow: "var(--brutal-shadow-sm)",
              }
            : {
                border: "1.5px solid #d7e4d9",
                backgroundColor: "#f4f8f3",
                color: "#335547",
              }}
          data-testid={weekendTestId}
          aria-pressed={guidePanel === "weekend"}
          aria-label={guidePanel === "weekend" ? "Close weekend guide" : "Open weekend guide"}
        >
          {guidePanel === "weekend" ? (
            <X className="h-3.5 w-3.5 shrink-0" />
          ) : (
            <BookOpen className="h-3.5 w-3.5 shrink-0" />
          )}
          This Weekend
          {guidePanel === "weekend" && (
            <span className="hidden text-[10px] font-semibold uppercase tracking-[0.08em] sm:inline">
              Close
            </span>
          )}
        </button>
      </div>
    </section>
  );
}

function EventsLoadingShell() {
  return (
    <main id="event-results" tabIndex={-1} aria-label="Event results" className="flex-1 outline-none">
      <div className="w-full px-4 lg:px-6 py-6">
        <div className="mb-6 h-24 animate-pulse rounded-2xl border border-border/80 bg-muted/35 lg:h-28" />
        <div className="space-y-4">
          {[0, 1, 2, 3].map((index) => (
            <div
              key={index}
              className="min-h-[156px] animate-pulse rounded-xl border border-border/80 bg-card/80 shadow-sm lg:min-h-[172px]"
            />
          ))}
        </div>
      </div>
    </main>
  );
}

// `params` is accepted (and unused) so this component remains assignable to
// wouter's `<Route component={Home} />` prop; date-intent routes render
// <Home routeDateIntent="..."/> directly instead.
export default function Home({ routeDateIntent }: { routeDateIntent?: DateIntentKey; params?: Record<string, string | undefined> } = {}) {
  const { toast } = useToast();
  const { isAdmin, user, isAuthenticated } = useAuth();
  const { data: userAccountsEnabled = true } = useBootstrapData((data) => data.features.userAccounts.enabled);
  const [location, setLocation] = useLocation();
  const { regionSlug: routeRegionSlug, categorySlug: routeCategorySlug } = useParams<{ regionSlug?: string; categorySlug?: string }>();
  const normalizedRouteRegionSlug = routeRegionSlug ? slugify(routeRegionSlug) : null;
  const normalizedRouteCategorySlug = routeCategorySlug ? slugify(routeCategorySlug) : null;
  // Date-intent landing routes (/today, /tomorrow, /this-weekend) resolve statically.
  const routeDateIntentConfig = routeDateIntent ? getDateIntentSeoConfigByKey(routeDateIntent) : undefined;
  const routeDateIntentPath = routeDateIntentConfig?.path ?? null;
  const [selectedDrawerEvent, setSelectedDrawerEvent] = useState<EventDrawerEvent | null>(null);
  const [guidePanel, setGuidePanel] = useState<"week" | "weekend" | null>(null);
  
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [excludedCategories, setExcludedCategories] = useState<string[]>([]);
  const [selectedSources, setSelectedSources] = useState<string[]>([]);
  const [excludedSources, setExcludedSources] = useState<string[]>([]);
  const [selectedRegions, setSelectedRegions] = useState<string[]>([]);
  const [excludedRegions, setExcludedRegions] = useState<string[]>([]);
  const [selectedSourceForUrl, setSelectedSourceForUrl] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string | null>(null);
  const [fullUrl, setFullUrl] = useState(window.location.pathname + window.location.search);
  const [dateFilter, setDateFilter] = useState<DateFilterType>(null);
  const [customDate, setCustomDate] = useState<string | null>(null);
  const [timePeriods, setTimePeriods] = useState<TimePeriod[]>(['allDay']);
  const [filtersRestored, setFiltersRestored] = useState(false);
  const [bookmarkBannerDismissed, setBookmarkBannerDismissed] = useState<boolean>(
    () => localStorage.getItem('bookmarkBannerDismissed') === 'true'
  );
  const [heroManuallyCollapsed, setHeroManuallyCollapsed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    if (window.matchMedia('(max-width: 767px)').matches) return true;
    const saved = localStorage.getItem(HERO_COLLAPSED_STORAGE_KEY);
    if (saved === 'true' || saved === 'false') {
      return saved === 'true';
    }
    return false;
  });
  const [pendingMobileBrowseSelection, setPendingMobileBrowseSelection] =
    useState<PendingMobileBrowseSelection | null>(readPendingMobileBrowseSelection);
  const [browseAnnouncement, setBrowseAnnouncement] = useState('');
  // Explore mode state (Events vs Sources vs Recently Viewed) with localStorage persistence
  // 'recently-viewed' is not persisted — it falls back to 'events' on reload
  const [exploreMode, setExploreMode] = useState<ExploreMode>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem(EXPLORE_MODE_KEY);
      if (saved === 'events' || saved === 'sources') {
        return saved;
      }
    }
    return 'events';
  });

  const handleExploreModeChange = (mode: ExploreMode) => {
    const previousMode = exploreMode;
    setExploreMode(mode);
    // Don't persist 'recently-viewed' — it should not survive a page reload
    if (mode !== 'recently-viewed') {
      localStorage.setItem(EXPLORE_MODE_KEY, mode);
    }
    trackViewSwitch(previousMode, mode);
    // Scroll to top on mode switch — desktop uses the inner scroll container,
    // mobile uses the document body scroll
    window.scrollTo({ top: 0 });
    // Close the filters panel when switching to sources — preserve all filter state
    if (mode === 'sources') {
      setFilterUiState('closed');
    }
  };

  // Surprise Me state
  const [viewMode, setViewMode] = useState<ViewMode>('HOME');
  const [randomEvent, setRandomEvent] = useState<Event | null>(null);
  const [lastRandomEventId, setLastRandomEventId] = useState<string | undefined>();
  const [savedScrollPosition, setSavedScrollPosition] = useState(0);
  const [previousFocusElement, setPreviousFocusElement] = useState<HTMLElement | null>(null);
  const surpriseCardRef = useRef<HTMLDivElement>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const searchBannerRef = useRef<HTMLDivElement>(null);
  const discoveryFiltersTriggerRef = useRef<HTMLButtonElement>(null);
  
  // One source of truth for filter UI state keeps mobile sheet and desktop drawer in sync.
  const [filterUiState, setFilterUiState] = useState<FilterUiState>('closed');
  const filtersOpen = filterUiState === 'mobile';
  const desktopDrawerOpen = filterUiState === 'desktop';

  const [sidebarExpandSection, setSidebarExpandSection] = useState<{ section: 'regions' | 'categories' | 'sources'; item?: string; ts: number } | null>(null);
  const [mobileExpandSection, setMobileExpandSection] = useState<{ section: 'regions' | 'categories' | 'sources'; ts: number } | null>(null);
  const [regionAlertDismissed, setRegionAlertDismissed] = useState(false); /* OAL-SOFTEN: change 12 */

  // Region quick-select override: one of the four city pills or null
  const [activeRegionOverride, setActiveRegionOverride] = useState<string | null>(null);
  const [filterNoticeDismissed, setFilterNoticeDismissed] = useState(false); /* OAL-FILTER: change 10B */
  const prevExploreModeRef = useRef<string>(exploreMode); /* OAL-FILTER: change 10B */
  // Reset amber strip dismissal whenever the excluded regions list changes length
  useEffect(() => { setRegionAlertDismissed(false); }, [excludedRegions.length]); /* OAL-SOFTEN: change 12 */

  // OAL-FILTER: change 10B — reset notice dismissed state when exploreMode switches away and back
  useEffect(() => {
    if (prevExploreModeRef.current !== exploreMode) {
      setFilterNoticeDismissed(false); /* OAL-FILTER: change 10B */
      prevExploreModeRef.current = exploreMode;
    }
  }, [exploreMode]);

  useEffect(() => {
    const syncFilterUiState = () => {
      setFilterUiState((current) => {
        if (current === 'mobile' && window.innerWidth >= 1024) return 'desktop';
        if (current === 'desktop' && window.innerWidth < 1024) return 'mobile';
        return current;
      });
    };

    syncFilterUiState();
    window.addEventListener('resize', syncFilterUiState);
    return () => window.removeEventListener('resize', syncFilterUiState);
  }, []);

  const setMobileFiltersOpen = (open: boolean) => {
    setFilterUiState(open ? 'mobile' : 'closed');
  };

  const closeFiltersDialog = useCallback(() => {
    setFilterUiState("closed");
  }, []);

  const setPersistedHeroCollapsed = useCallback((collapsed: boolean) => {
    setHeroManuallyCollapsed(collapsed);
    localStorage.setItem(HERO_COLLAPSED_STORAGE_KEY, collapsed ? 'true' : 'false');
  }, []);

  const heroCollapsed = heroManuallyCollapsed;
  const [filtersGlowPulseKey, setFiltersGlowPulseKey] = useState(0);

  const handleHeroCollapse = useCallback(() => {
    trackFilterUiInteraction({
      interactionType: 'discovery_panel_collapse',
      surface: 'discovery_panel',
      label: 'Collapse discovery',
    });
    setPersistedHeroCollapsed(true);
    setFiltersGlowPulseKey((current) => current + 1);
  }, [setPersistedHeroCollapsed, trackFilterUiInteraction]);

  const handleHeroExpand = useCallback(() => {
    trackFilterUiInteraction({
      interactionType: 'discovery_panel_expand',
      surface: 'discovery_panel',
      label: 'Browse regions & categories',
    });
    setGuidePanel(null);
    setPersistedHeroCollapsed(false);
    if (window.innerWidth >= 768) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [setPersistedHeroCollapsed, trackFilterUiInteraction]);

  const handleGuideButton = useCallback((type: "week" | "weekend") => {
    trackGuideButtonInteraction(type);
    setGuidePanel((current) => {
      if (current === type) {
        return null;
      }

      setPersistedHeroCollapsed(true);
      return type;
    });
  }, [setPersistedHeroCollapsed]);

  const closeFilterUi = () => {
    setFilterUiState('closed');
  };

  const openDesktopDrawer = () => {
    setFilterUiState('desktop');
  };

  const toggleDesktopDrawer = () => {
    setFilterUiState((current) => (current === 'desktop' ? 'closed' : 'desktop'));
  };
  
  const { isPlaying: isAutoScrollPlaying, togglePlay: toggleAutoScroll } = useAutoScroll();
  const dateHeaderRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const [mobileHeaderCollapsed, setMobileHeaderCollapsed] = useState(false);
  const mobileHeaderScrollRef = useRef({
    lastScrollY: 0,
    ticking: false,
    collapsed: false,
    ignoreScrollUpUntil: 0,
    holdGeneration: 0,
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const mediaQuery = window.matchMedia('(max-width: 767px)');
    const scrollState = mobileHeaderScrollRef.current;
    let cleanupScroll: (() => void) | null = null;

    const applyScrollState = () => {
      const currentScrollY = Math.max(window.scrollY, 0);
      if (isHeaderHeld() || scrollState.holdGeneration !== headerHoldGeneration()) {
        // A time-of-day jump is scrolling (or just scrolled) the page: keep the header, and the
        // bar under it, where they are, and start counting the visitor's scrolling from here
        scrollState.holdGeneration = headerHoldGeneration();
        scrollState.lastScrollY = currentScrollY;
        scrollState.ticking = false;
        return;
      }
      const previousScrollY = scrollState.lastScrollY;
      const now = window.performance.now();
      const scrollingUp = currentScrollY < previousScrollY && now > scrollState.ignoreScrollUpUntil;

      let nextCollapsed: boolean | null = null;
      if (currentScrollY <= 30 || scrollingUp) {
        nextCollapsed = false;
      } else if (currentScrollY > 80 && currentScrollY > previousScrollY) {
        nextCollapsed = true;
      } else if (currentScrollY <= 80 && !scrollState.collapsed) {
        nextCollapsed = false;
      }

      if (nextCollapsed !== null) {
        scrollState.collapsed = nextCollapsed;
        if (nextCollapsed) {
          scrollState.ignoreScrollUpUntil = now + 320;
        }
        setMobileHeaderCollapsed((current) => (
          current === nextCollapsed ? current : nextCollapsed
        ));
      }

      scrollState.lastScrollY = currentScrollY;
      scrollState.ticking = false;
    };

    const handleScroll = () => {
      if (scrollState.ticking) return;
      scrollState.ticking = true;
      window.requestAnimationFrame(applyScrollState);
    };

    const enableMobileScroll = () => {
      scrollState.lastScrollY = Math.max(window.scrollY, 0);
      window.addEventListener('scroll', handleScroll, { passive: true });
      cleanupScroll = () => {
        window.removeEventListener('scroll', handleScroll);
        cleanupScroll = null;
      };
      applyScrollState();
    };

    const syncMobileListener = () => {
      cleanupScroll?.();
      scrollState.ticking = false;

      if (mediaQuery.matches) {
        enableMobileScroll();
      } else {
        scrollState.collapsed = false;
        setMobileHeaderCollapsed(false);
      }
    };

    syncMobileListener();
    mediaQuery.addEventListener('change', syncMobileListener);

    return () => {
      cleanupScroll?.();
      mediaQuery.removeEventListener('change', syncMobileListener);
      scrollState.ticking = false;
    };
  }, []);
  
  // Known filter param keys that we manage
  const MANAGED_PARAMS = ['q', 'categories', 'excludedCategories', 'sources', 'excludedSources', 'regions', 'excludedRegions', 'date', 'customDate', 'time', 'selectedSource'];
  
  // Track if we're currently syncing URL to prevent loops
  const isSyncingUrlRef = useRef(false);

  // Helper to create a serialized filter signature for comparison
  const createFilterSignature = useCallback((
    query: string | null,
    cats: string[],
    excluded: string[],
    sources: string[],
    excSources: string[],
    regions: string[],
    excRegions: string[],
    baseRegion: string | null,
    baseCategory: string | null,
    baseDateIntent: DateIntentKey | null,
    date: DateFilterType,
    custom: string | null,
    time: TimePeriod[]
  ) => {
    return JSON.stringify({
      q: query || '',
      categories: [...cats].sort(),
      excludedCategories: [...excluded].sort(),
      sources: [...sources].sort(),
      excludedSources: [...excSources].sort(),
      regions: [...regions].sort(),
      excludedRegions: [...excRegions].sort(),
      baseRegion: baseRegion || '',
      baseCategory: baseCategory || '',
      baseDateIntent: baseDateIntent || '',
      date: date || '',
      customDate: date === 'custom' ? (custom || '') : '',
      time: time.length === 1 && time[0] === 'allDay' ? [] : [...time].sort()
    });
  }, []);

  // Track the last filter signature we set in URL
  const lastFilterSignatureRef = useRef<string>('');
  // The page (town|category|date) the URL sync last ran for, to spot the first run after a page change
  const syncedRouteKeyRef = useRef<string | null>(null);

  // Helper to build URL query string from filter state, preserving unknown params from provided base
  const buildFilterUrl = useCallback((
    basePath: string,
    baseSearch: string,
    query: string | null,
    cats: string[],
    excluded: string[],
    sources: string[],
    excSources: string[],
    regions: string[],
    excRegions: string[],
    baseRegion: string | null,
    baseCategory: string | null,
    baseDateIntent: DateIntentKey | null,
    date: DateFilterType,
    custom: string | null,
    time: TimePeriod[]
  ) => {
    const params = new URLSearchParams(baseSearch);

    // Remove all managed params first, then set new values
    MANAGED_PARAMS.forEach(key => params.delete(key));

    // Set filter values
    if (query) params.set('q', query);
    cats.filter(cat => !baseCategory || cat !== baseCategory).forEach(cat => params.append('categories', cat));
    excluded.forEach(cat => params.append('excludedCategories', cat));
    sources.forEach(s => params.append('sources', s));
    excSources.forEach(s => params.append('excludedSources', s));
    regions.filter(region => !baseRegion || region !== baseRegion).forEach(region => params.append('regions', region));
    excRegions.forEach(region => params.append('excludedRegions', region));
    if (date && date !== baseDateIntent) params.set('date', date);
    if (date === 'custom' && custom) params.set('customDate', custom);
    const isDefaultTime = time.length === 1 && time[0] === 'allDay';
    if (!isDefaultTime) params.set('time', time.join(','));
    
    const queryString = params.toString();
    return queryString ? `${basePath}?${queryString}` : basePath;
  }, []);

  // Fetch all regions for excluded-model pill clicks and region route resolution
  const { data: regionsData, isLoading: regionsLoading } = useQuery<{ regions: { region: string }[] }>({
    queryKey: ['/api/regions'],
    staleTime: 60 * 1000,
  });
  const allRegionNames = useMemo(() => regionsData?.regions.map(r => r.region) ?? [], [regionsData]);

  // Fetch master categories with their raw category mappings for filtering and category route resolution
  const { data: categoryFilters = [], isLoading: categoriesLoading } = useQuery<Array<{ masterCategory: MasterCategory; rawCategories: string[] }>>({
    queryKey: ['/api/categories/filters'],
  });
  const masterCategoryNames = categoryFilters.map(cf => cf.masterCategory.name.trim());

  const routeRegionName = useMemo(() => {
    if (!normalizedRouteRegionSlug || allRegionNames.length === 0) return null;
    return allRegionNames.find((region) => slugify(region) === normalizedRouteRegionSlug) ?? null;
  }, [allRegionNames, normalizedRouteRegionSlug]);
  const routeRegionPath = routeRegionName ? getRegionPathFromName(routeRegionName) : (normalizedRouteRegionSlug ? `/regions/${normalizedRouteRegionSlug}` : null);
  const routeCategoryConfig = normalizedRouteCategorySlug ? getCategorySeoConfigBySlug(normalizedRouteCategorySlug) : undefined;
  const routeCategoryName = useMemo(() => {
    if (!normalizedRouteCategorySlug) return null;
    if (routeCategoryConfig) return routeCategoryConfig.name;
    if (masterCategoryNames.length === 0) return null;
    return masterCategoryNames.find((category) => slugify(category) === normalizedRouteCategorySlug) ?? null;
  }, [masterCategoryNames, normalizedRouteCategorySlug, routeCategoryConfig]);
  const routeCategoryPath = routeCategoryName ? getCategoryPathFromName(routeCategoryName) : (normalizedRouteCategorySlug ? `/categories/${normalizedRouteCategorySlug}` : null);

  // Helper to parse URL params into filter state
  const parseFiltersFromUrl = useCallback(() => {
    // Skip if we're in the middle of syncing URL from state
    if (isSyncingUrlRef.current) return;
    
    const params = new URLSearchParams(window.location.search);
    
    // Parse all filter values
    const query = params.get('q')?.trim() || null;
    
    const categoriesParam = params.getAll('categories');
    const cats = [...new Set(categoriesParam.map(c => c.trim()).filter(Boolean))];
    const categorySelection = routeCategoryName
      ? [...new Set([routeCategoryName, ...cats])]
      : cats;
    
    const excludedParam = params.getAll('excludedCategories');
    const excluded = [...new Set(excludedParam.map(c => c.trim()).filter(Boolean))];
    
    const sourcesParam = params.getAll('sources');
    let sources = sourcesParam.length > 0
      ? [...new Set(sourcesParam.map(x => x.trim()).filter(Boolean))]
      : [];
    
    const excludedSourcesParam = params.getAll('excludedSources');
    let excSources = excludedSourcesParam.length > 0
      ? [...new Set(excludedSourcesParam.map(x => x.trim()).filter(Boolean))]
      : [];

    // Handle compact selectedSource param (single-source navigation from Events by Source tab)
    const selectedSourceVal = params.get('selectedSource')?.trim() || null;
    if (selectedSourceVal) {
      setSelectedSourceForUrl(selectedSourceVal);
      sources = [selectedSourceVal];
      excSources = [];
    } else {
      setSelectedSourceForUrl(null);
    }
    
    const regionsParam = params.getAll('regions');
    const regions = [...new Set(regionsParam.map(r => r.trim()).filter(Boolean))];
    const regionSelection = routeRegionName
      ? [...new Set([routeRegionName, ...regions])]
      : regions;
    
    const excludedRegionsParam = params.getAll('excludedRegions');
    const excRegions = [...new Set(excludedRegionsParam.map(r => r.trim()).filter(Boolean))];
    
    const dateParam = params.get('date') as DateFilterType;
    const date = dateParam && ['today', 'tomorrow', 'weekend', 'custom'].includes(dateParam)
      ? dateParam
      : (routeDateIntent ?? null);
    
    const customDateParam = params.get('customDate');
    const custom = date === 'custom' && customDateParam && isValidDateString(customDateParam) 
      ? customDateParam 
      : null;
    
    let time: TimePeriod[] = ['allDay'];
    const timeParam = params.get('time');
    if (timeParam) {
      const validPeriods: TimePeriod[] = ['morning', 'afternoon', 'evening', 'allDay'];
      const periods = timeParam.split(',').map(s => s.trim()).filter(p => validPeriods.includes(p as TimePeriod)) as TimePeriod[];
      if (periods.length > 0) time = periods;
    }
    
    // Create signature of parsed filters
    const newSignature = createFilterSignature(query, categorySelection, excluded, sources, excSources, regionSelection, excRegions, routeRegionName, routeCategoryName, routeDateIntent ?? null, date, custom, time);
    
    // Skip if signature matches what we last set (prevents loop)
    if (newSignature === lastFilterSignatureRef.current) return;
    
    // Update state
    setSearchQuery(query);
    setSelectedCategories(categorySelection);
    setExcludedCategories(excluded);
    setSelectedSources(sources);
    setExcludedSources(excSources);
    setSelectedRegions(regionSelection);
    setExcludedRegions(excRegions);
    setDateFilter(date);
    setCustomDate(custom);
    setTimePeriods(time);
    
    // Auto-switch to events view when URL has sources filter (e.g., from "View events" button)
    if (sources.length > 0 || excSources.length > 0 || selectedSourceVal) {
      setExploreMode('events');
      localStorage.setItem(EXPLORE_MODE_KEY, 'events');
    }
    
    // Update signature to match parsed state
    lastFilterSignatureRef.current = newSignature;
  }, [createFilterSignature, routeCategoryName, routeRegionName, routeDateIntent]);

  // Parse filters from URL on mount and listen for navigation changes
  useEffect(() => {
    // Parse URL first — sets state and lastFilterSignatureRef from URL params (or empty if none)
    parseFiltersFromUrl();
    setFullUrl(window.location.pathname + window.location.search);

    // Restore saved filters from localStorage ONLY when the URL carries no filter params.
    // Running AFTER parseFiltersFromUrl ensures the restore overwrites the empty state
    // rather than being wiped by parseFiltersFromUrl immediately afterward.
    // We intentionally do NOT set lastFilterSignatureRef here — the URL sync effect will
    // detect the mismatch and update the URL via replaceState to reflect the restored filters,
    // making the state bookmarkable. The polling loop then stabilises on the next tick.
    if (!urlHasFilterParams() && !routeRegionSlug && !routeCategorySlug && !routeDateIntent) {
      try {
        const raw = localStorage.getItem(FILTER_STORAGE_KEY);
        if (raw) {
          const saved: SavedFilters = JSON.parse(raw);
          if (saved.version === 1) {
            const savedAt = new Date(saved.savedAt);
            const ageDays = (Date.now() - savedAt.getTime()) / (1000 * 60 * 60 * 24);
            if (ageDays <= FILTER_STORAGE_EXPIRY_DAYS) {
              const todayIso = new Date().toLocaleDateString('en-CA');
              let dateFilter = saved.dateFilter;
              let customDate = saved.customDate;
              if (dateFilter === 'custom' && customDate && customDate < todayIso) {
                dateFilter = null;
                customDate = null;
              }
              setSelectedCategories(saved.selectedCategories ?? []);
              setExcludedCategories(saved.excludedCategories ?? []);
              setSelectedSources(saved.selectedSources ?? []);
              setExcludedSources(saved.excludedSources ?? []);
              setSelectedRegions(saved.selectedRegions ?? []);
              setExcludedRegions(saved.excludedRegions ?? []);
              setDateFilter(dateFilter);
              setCustomDate(customDate);
              setTimePeriods(saved.timePeriods?.length ? saved.timePeriods : ['allDay']);
            }
          }
        }
      } catch {
        // Malformed data — silently ignore
      }
    }

    setFiltersRestored(true);
    
    // Listen for popstate events (back/forward button)
    const handlePopState = () => {
      setFullUrl(window.location.pathname + window.location.search);
      // Clear signature to force reparse
      lastFilterSignatureRef.current = '';
      parseFiltersFromUrl();
    };
    
    // Track last URL for polling comparison
    let lastCheckedUrl = window.location.pathname + window.location.search;
    
    // Poll for URL changes (needed for wouter setLocation which doesn't trigger popstate)
    const handleLocationChange = () => {
      const newUrl = window.location.pathname + window.location.search;
      if (newUrl !== lastCheckedUrl) {
        lastCheckedUrl = newUrl;
        setFullUrl(newUrl);
        parseFiltersFromUrl();
      }
    };
    
    window.addEventListener('popstate', handlePopState);
    const intervalId = setInterval(handleLocationChange, 100);
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
      clearInterval(intervalId);
    };
  }, [parseFiltersFromUrl]);

  // Sync filter state to URL when filters change
  useEffect(() => {
    if (routeRegionSlug && !routeRegionName) return;
    if (routeCategorySlug && !routeCategoryName) return;

    // The first run after a page change still sees the previous page's filters (the new
    // address is read into state on the next render), so that run never leaves a date page.
    const routeKey = `${routeRegionName ?? ''}|${routeCategoryName ?? ''}|${routeDateIntent ?? ''}`;
    const stateMatchesRoute = syncedRouteKeyRef.current === routeKey;
    syncedRouteKeyRef.current = routeKey;

    // Create signature from current state
    const currentSignature = createFilterSignature(
      searchQuery,
      selectedCategories,
      excludedCategories,
      selectedSources,
      excludedSources,
      selectedRegions,
      excludedRegions,
      routeRegionName,
      routeCategoryName,
      routeDateIntent ?? null,
      dateFilter,
      customDate,
      timePeriods
    );

    // Skip if signature hasn't changed from what we last set
    if (currentSignature === lastFilterSignatureRef.current) return;

    let newUrl: string;

    // When in single-source mode, emit a compact /?selectedSource=X URL instead of 83 excludedSources params
    // Region x date-intent routes combine both path segments (/regions/troy/this-weekend).
    // A date page whose date was removed or changed moves to / or the town page.
    const { basePath, baseDateIntent } = getFilterUrlBase(
      {
        regionPath: routeRegionPath,
        categoryPath: routeCategoryPath,
        dateIntent: routeDateIntent ?? null,
        dateIntentPath: routeDateIntentPath,
      },
      stateMatchesRoute ? dateFilter : (routeDateIntent ?? null),
    );
    if (selectedSourceForUrl) {
      const compactParams = new URLSearchParams();
      compactParams.set('selectedSource', selectedSourceForUrl);
      if (searchQuery) compactParams.set('q', searchQuery);
      selectedCategories
        .filter(c => !routeCategoryName || c !== routeCategoryName)
        .forEach(c => compactParams.append('categories', c));
      excludedCategories.forEach(c => compactParams.append('excludedCategories', c));
      selectedRegions.filter(r => !routeRegionName || r !== routeRegionName).forEach(r => compactParams.append('regions', r));
      excludedRegions.forEach(r => compactParams.append('excludedRegions', r));
      if (dateFilter && dateFilter !== baseDateIntent) compactParams.set('date', dateFilter);
      if (dateFilter === 'custom' && customDate) compactParams.set('customDate', customDate);
      const isDefaultTime = timePeriods.length === 1 && timePeriods[0] === 'allDay';
      if (!isDefaultTime) compactParams.set('time', timePeriods.join(','));
      newUrl = compactParams.toString() ? `${basePath}?${compactParams.toString()}` : basePath;
    } else {
      // Build new URL preserving unknown params from the current URL
      const baseSearch = window.location.search ? window.location.search.slice(1) : '';
      newUrl = buildFilterUrl(
        basePath,
        baseSearch,
        searchQuery,
        selectedCategories,
        excludedCategories,
        selectedSources,
        excludedSources,
        selectedRegions,
        excludedRegions,
        routeRegionName,
        routeCategoryName,
        baseDateIntent,
        dateFilter,
        customDate,
        timePeriods
      );
    }
    
    // Skip if URL is already correct
    const currentUrl = window.location.pathname + window.location.search;
    if (newUrl === currentUrl) {
      lastFilterSignatureRef.current = currentSignature;
      return;
    }
    
    // Update URL
    isSyncingUrlRef.current = true;
    lastFilterSignatureRef.current = currentSignature;
    window.history.replaceState({}, '', newUrl);
    setFullUrl(newUrl);
    
    // Clear sync flag after microtask to allow state to settle
    queueMicrotask(() => {
      isSyncingUrlRef.current = false;
    });
  }, [searchQuery, selectedCategories, excludedCategories, selectedSources, excludedSources, selectedRegions, excludedRegions, routeRegionSlug, routeRegionName, routeRegionPath, routeCategorySlug, routeCategoryName, routeCategoryPath, routeDateIntent, routeDateIntentPath, dateFilter, customDate, timePeriods, selectedSourceForUrl, createFilterSignature, buildFilterUrl]);

  // Landing-page state for region routes: keep the canonical region selected unless another filter is active.
  // Time filter is active when the array is NOT exactly ['allDay'] (default state)
  const isDefaultTimeFilter = timePeriods.length === 1 && timePeriods[0] === 'allDay';
  const isPureRegionLandingPage = !!routeRegionName &&
    searchQuery === null &&
    selectedCategories.length === 0 &&
    excludedCategories.length === 0 &&
    selectedSources.length === 0 &&
    excludedSources.length === 0 &&
    selectedRegions.length === 1 &&
    selectedRegions[0] === routeRegionName &&
    excludedRegions.length === 0 &&
    dateFilter === null &&
    isDefaultTimeFilter;
  const isPureCategoryLandingPage = !!routeCategoryName &&
    searchQuery === null &&
    selectedCategories.length === 1 &&
    selectedCategories[0] === routeCategoryName &&
    excludedCategories.length === 0 &&
    selectedSources.length === 0 &&
    excludedSources.length === 0 &&
    selectedRegions.length === 0 &&
    excludedRegions.length === 0 &&
    dateFilter === null &&
    isDefaultTimeFilter;
  // Landing-page state for date-intent routes (/today, /tomorrow, /this-weekend):
  // the route's date filter stays applied unless another filter is active.
  const isPureDateIntentLandingPage = !!routeDateIntent &&
    !routeRegionName &&
    searchQuery === null &&
    selectedCategories.length === 0 &&
    excludedCategories.length === 0 &&
    selectedSources.length === 0 &&
    excludedSources.length === 0 &&
    selectedRegions.length === 0 &&
    excludedRegions.length === 0 &&
    dateFilter === routeDateIntent &&
    isDefaultTimeFilter;
  // Landing-page state for region x date-intent routes (/regions/troy/this-weekend):
  // both the region and the date filter stay applied unless another filter is active.
  const isPureRegionDateIntentLandingPage = !!routeRegionName &&
    !!routeDateIntent &&
    searchQuery === null &&
    selectedCategories.length === 0 &&
    excludedCategories.length === 0 &&
    selectedSources.length === 0 &&
    excludedSources.length === 0 &&
    selectedRegions.length === 1 &&
    selectedRegions[0] === routeRegionName &&
    excludedRegions.length === 0 &&
    dateFilter === routeDateIntent &&
    isDefaultTimeFilter;

  // Persist non-search filters to localStorage so they survive between visits
  const saveDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (routeRegionSlug && !routeRegionName) return;
    if (routeCategorySlug && !routeCategoryName) return;

    if (saveDebounceRef.current) clearTimeout(saveDebounceRef.current);
    saveDebounceRef.current = setTimeout(() => {
      const isDefaultTime = timePeriods.length === 1 && timePeriods[0] === 'allDay';
      const hasAnyFilter =
        !isPureRegionLandingPage && !isPureCategoryLandingPage && !isPureDateIntentLandingPage && !isPureRegionDateIntentLandingPage && (
          selectedCategories.length > 0 ||
          excludedCategories.length > 0 ||
          selectedSources.length > 0 ||
          excludedSources.length > 0 ||
          selectedRegions.length > 0 ||
          excludedRegions.length > 0 ||
          dateFilter !== null ||
          !isDefaultTime
        );
      if (hasAnyFilter) {
        const toSave: SavedFilters = {
          version: 1,
          savedAt: new Date().toISOString(),
          selectedCategories,
          excludedCategories,
          selectedSources,
          excludedSources,
          selectedRegions,
          excludedRegions,
          dateFilter,
          customDate,
          timePeriods,
        };
        localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(toSave));
      } else {
        localStorage.removeItem(FILTER_STORAGE_KEY);
      }
    }, 500);
    return () => {
      if (saveDebounceRef.current) clearTimeout(saveDebounceRef.current);
    };
  }, [selectedCategories, excludedCategories, selectedSources, excludedSources, selectedRegions, excludedRegions, dateFilter, customDate, timePeriods, isPureRegionLandingPage, isPureCategoryLandingPage, isPureDateIntentLandingPage, isPureRegionDateIntentLandingPage, routeRegionSlug, routeRegionName, routeCategorySlug, routeCategoryName]);

  // Measure search banner height dynamically to avoid hardcoded offset
  // Re-run when searchQuery, viewMode, or filters change (banner shows for search OR filters)
  const hasActiveFilters = !isPureRegionLandingPage && !isPureCategoryLandingPage && !isPureDateIntentLandingPage && !isPureRegionDateIntentLandingPage && (
    selectedCategories.length > 0 ||
    excludedCategories.length > 0 ||
    selectedSources.length > 0 ||
    excludedSources.length > 0 ||
    selectedRegions.length > 0 ||
    excludedRegions.length > 0 ||
    dateFilter !== null ||
    !isDefaultTimeFilter
  );
  
  // Count active facet groups, not every included/excluded value.
  const activeFilterCount = hasActiveFilters
    ? Number(selectedRegions.length > 0 || excludedRegions.length > 0) +
      Number(selectedCategories.length > 0 || excludedCategories.length > 0) +
      Number(selectedSources.length > 0 || excludedSources.length > 0) +
      Number(dateFilter !== null) +
      Number(!isDefaultTimeFilter)
    : 0;

  // Funnel context fields recorded alongside each click event
  const clickContextValue = useMemo(() => {
    if (searchQuery && searchQuery.trim().length > 0) return 'search_results' as const;
    if (hasActiveFilters) return 'filtered_browse' as const;
    return 'discovery_chip_browse' as const;
  }, [searchQuery, hasActiveFilters]);

  const filtersJsonValue = useMemo<Record<string, unknown> | null>(() => {
    const obj: Record<string, unknown> = {};
    if (selectedCategories.length) obj.categories = selectedCategories;
    if (excludedCategories.length) obj.excludedCategories = excludedCategories;
    if (selectedSources.length) obj.sources = selectedSources;
    if (selectedRegions.length) obj.regions = selectedRegions;
    if (dateFilter) obj.dateFilter = dateFilter;
    if (customDate) obj.customDate = customDate;
    if (timePeriods.length && !(timePeriods.length === 1 && timePeriods[0] === 'allDay')) obj.timePeriods = timePeriods;
    return Object.keys(obj).length ? obj : null;
  }, [selectedCategories, excludedCategories, selectedSources, selectedRegions, dateFilter, customDate, timePeriods]);

  const listInstanceId = useMemo(() => {
    return [
      clickContextValue,
      (searchQuery || '').trim().toLowerCase(),
      filtersJsonValue ? JSON.stringify(filtersJsonValue) : '',
    ].join('|');
  }, [clickContextValue, searchQuery, filtersJsonValue]);

  const seoMeta = useMemo(() => {
    const isDefaultListing = !searchQuery && !hasActiveFilters;
    const hasOnlySingleCategoryFilter =
      !searchQuery &&
      selectedCategories.length === 1 &&
      excludedCategories.length === 0 &&
      selectedSources.length === 0 &&
      excludedSources.length === 0 &&
      selectedRegions.length === 0 &&
      excludedRegions.length === 0 &&
      dateFilter === null &&
      isDefaultTimeFilter;

    if (isPureRegionDateIntentLandingPage && routeRegionName && routeDateIntentConfig) {
      const combinedMeta = buildRegionDateIntentMeta(routeRegionName, routeDateIntentConfig);
      return {
        title: combinedMeta.title,
        description: combinedMeta.description,
        canonical: `${HOME_BASE_URL}${getRegionDateIntentPath(routeRegionName, routeDateIntentConfig)}`,
        robots: isNonTownRegion(routeRegionName) ? 'noindex, follow' : 'index, follow',
        image: SHARE_IMAGE_URL,
      };
    }

    if (isPureRegionLandingPage && routeRegionName) {
      return {
        title: getRegionPageTitle(routeRegionName),
        description: getRegionPageDescription(routeRegionName),
        canonical: `${HOME_BASE_URL}${routeRegionPath ?? getRegionPathFromName(routeRegionName)}`,
        robots: isNonTownRegion(routeRegionName) ? 'noindex, follow' : 'index, follow',
        image: SHARE_IMAGE_URL,
      };
    }

    if (isPureCategoryLandingPage && routeCategoryName) {
      const categoryConfig = getCategorySeoConfigByName(routeCategoryName);
      return {
        title: categoryConfig?.title ?? `${routeCategoryName} Events in Albany & the Capital District | On A Lark`,
        description:
          categoryConfig?.description ??
          `Browse upcoming ${routeCategoryName.toLowerCase()} events in Albany, Troy, Schenectady, Saratoga, and across the Capital District on On A Lark.`,
        canonical: `${HOME_BASE_URL}${routeCategoryPath ?? getCategoryPathFromName(routeCategoryName)}`,
        robots: 'index, follow',
        image: SHARE_IMAGE_URL,
      };
    }

    if (isPureDateIntentLandingPage && routeDateIntentConfig) {
      return {
        title: routeDateIntentConfig.title,
        description: routeDateIntentConfig.description,
        canonical: `${HOME_BASE_URL}${routeDateIntentConfig.path}`,
        robots: 'index, follow',
        image: SHARE_IMAGE_URL,
      };
    }

    if (hasOnlySingleCategoryFilter) {
      const categoryName = selectedCategories[0];
      const categoryConfig = getCategorySeoConfigByName(categoryName);
      return {
        title: categoryConfig?.title ?? `${categoryName} Events in Albany & the Capital District | On A Lark`,
        description:
          categoryConfig?.description ??
          `Browse upcoming ${categoryName.toLowerCase()} events in Albany, Troy, Schenectady, Saratoga, and across the Capital District on On A Lark.`,
        canonical: `${HOME_BASE_URL}${getCategoryPathFromName(categoryName)}`,
        robots: 'index, follow',
        image: SHARE_IMAGE_URL,
      };
    }

    if (isDefaultListing) {
      return {
        title: HOME_PAGE_TITLE,
        description: HOME_PAGE_DESCRIPTION,
        canonical: `${HOME_BASE_URL}/`,
        robots: 'index, follow',
        image: SHARE_IMAGE_URL,
      };
    }

    return {
      title: 'Filtered Albany & Capital District Events | On a Lark',
      description: 'Browse local events filtered by category, source, region, or date across Albany and the Capital District.',
      canonical: `${HOME_BASE_URL}/`,
      robots: 'noindex, follow',
      image: SHARE_IMAGE_URL,
    };
  }, [
    searchQuery,
    hasActiveFilters,
    selectedCategories,
    excludedCategories,
    selectedSources,
    excludedSources,
    selectedRegions,
    excludedRegions,
    dateFilter,
    isDefaultTimeFilter,
    isPureRegionLandingPage,
    isPureCategoryLandingPage,
    isPureDateIntentLandingPage,
    isPureRegionDateIntentLandingPage,
    routeRegionName,
    routeRegionPath,
    routeCategoryName,
    routeCategoryPath,
    routeDateIntentConfig,
  ]);


  // OAL-DORMANT: change 3 — passive inline notice when By Source/Recent active AND filters are set
  const showFilterNotice = (exploreMode === 'sources' || exploreMode === 'recently-viewed') && hasActiveFilters && !filterNoticeDismissed; /* OAL-DORMANT: change 3 */
  const showEventsDiscoveryHero = exploreMode === 'events';
  const showExploreModeTabs = true;
  const renderLegacyDiscovery = false;
  const renderLegacyFilterAlerts = false;
  const stickyDiscoveryStrip = showEventsDiscoveryHero && heroCollapsed;
  const hasIncludeCategories = selectedCategories.length > 0;
  const hasExcludeCategories = excludedCategories.length > 0;
  const hasSourceFilters = selectedSources.length > 0;
  const hasExcludeSourceFilters = excludedSources.length > 0;
  const hasRegionFilters = selectedRegions.length > 0;
  const hasExcludeRegionFilters = excludedRegions.length > 0;
  const hasDateFilter = dateFilter !== null;
  const hasTimeFilter = !(timePeriods.length === 1 && timePeriods[0] === 'allDay');
  const hasNonTextFilters = hasIncludeCategories || hasExcludeCategories || hasSourceFilters || hasExcludeSourceFilters || hasRegionFilters || hasExcludeRegionFilters || hasDateFilter || hasTimeFilter;
  const showSummaryContextLine = Boolean(searchQuery || hasNonTextFilters) && viewMode === 'HOME' && !filtersOpen && exploreMode === 'events';
  const dateLabel = useMemo(() => {
    if (!dateFilter) return null;
    switch (dateFilter) {
      case 'today':
        return 'Today';
      case 'tomorrow':
        return 'Tomorrow';
      case 'weekend':
        return 'Weekend';
      case 'custom':
        return customDate ? new Date(`${customDate}T12:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : null;
      default:
        return null;
    }
  }, [customDate, dateFilter]);
  const timePeriodLabels = useMemo(() => (
    timePeriods
      .filter(period => period !== 'allDay')
      .map(period => (
        period === 'morning'
          ? { key: period, label: 'Morning' }
          : period === 'afternoon'
            ? { key: period, label: 'Afternoon' }
            : { key: period, label: 'Evening' }
      ))
  ), [timePeriods]);

  // Calculate date filter dates for use in query
  const filterDates = getDateFilterDates(dateFilter, customDate);
  
  // Fetch all events with infinite scroll pagination
  // All filters are passed to server so pagination works on the filtered dataset
  const PAGE_SIZE = 50; // Reduced from 100 for faster initial load
  
  // Build serialized filter signature for debouncing (string for stable comparison)
  // Sort arrays to ensure stable keys regardless of selection order
  const filterSignature = useMemo(() => JSON.stringify({
    dates: [...filterDates].sort(),
    sources: [...selectedSources].sort(),
    excludedSources: [...excludedSources].sort(),
    categories: [...selectedCategories].sort(),
    excludedCategories: [...excludedCategories].sort(),
    timePeriods: !isDefaultTimeFilter ? [...timePeriods].sort() : null,
    regions: [...selectedRegions].sort(),
    excludedRegions: [...excludedRegions].sort(),
    baseRegion: routeRegionName || ''
  }), [filterDates, selectedSources, excludedSources, selectedCategories, excludedCategories, isDefaultTimeFilter, timePeriods, selectedRegions, excludedRegions, routeRegionName]);

  // Debounce filter changes so rapid toggling doesn't trigger multiple API requests
  const filterDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [debouncedSignature, setDebouncedSignature] = useState(filterSignature);
  
  useEffect(() => {
    if (filterDebounceRef.current) {
      clearTimeout(filterDebounceRef.current);
    }
    filterDebounceRef.current = setTimeout(() => {
      setDebouncedSignature(filterSignature);
    }, 250);
    return () => {
      if (filterDebounceRef.current) {
        clearTimeout(filterDebounceRef.current);
      }
    };
  }, [filterSignature]);

  // Parse the debounced signature back into filter values for the query
  const debouncedFilters = useMemo(() => JSON.parse(debouncedSignature) as {
    dates: string[];
    sources: string[];
    excludedSources: string[];
    categories: string[];
    excludedCategories: string[];
    timePeriods: string[] | null;
    regions: string[];
    excludedRegions: string[];
  }, [debouncedSignature]);

  // Serialised query-string for /api/events/counts-by-date — same filters as the
  // main events query but WITHOUT dates (we want counts across all upcoming dates).
  const countsFilterQueryString = useMemo(() => {
    const params = new URLSearchParams();
    debouncedFilters.sources.forEach(s => params.append('sources', s));
    debouncedFilters.excludedSources.forEach(s => params.append('excludedSources', s));
    debouncedFilters.categories.forEach(cat => params.append('categories', cat));
    debouncedFilters.excludedCategories.forEach(cat => params.append('excludedCategories', cat));
    debouncedFilters.regions.forEach(region => params.append('regions', region));
    debouncedFilters.excludedRegions.forEach(region => params.append('excludedRegions', region));
    if (debouncedFilters.timePeriods) {
      params.set('timePeriods', debouncedFilters.timePeriods.join(','));
    }
    return params.toString();
  }, [debouncedFilters]);
  
  const {
    data: eventsData,
    isLoading: eventsLoading,
    isFetching: eventsFetching,
    isFetchingNextPage,
    isFetchNextPageError,
    hasNextPage,
    fetchNextPage
  } = useInfiniteQuery({
    queryKey: ['/api/events', debouncedSignature],
    enabled: filtersRestored,
    queryFn: async ({ pageParam = 0 }) => {
      const params = new URLSearchParams();
      params.set('limit', PAGE_SIZE.toString());
      params.set('offset', pageParam.toString());
      
      if (debouncedFilters.dates.length > 0) {
        params.set('dates', debouncedFilters.dates.join(','));
      }
      debouncedFilters.sources.forEach(s => params.append('sources', s));
      debouncedFilters.excludedSources.forEach(s => params.append('excludedSources', s));
      debouncedFilters.categories.forEach(cat => params.append('categories', cat));
      debouncedFilters.excludedCategories.forEach(cat => params.append('excludedCategories', cat));
      debouncedFilters.regions.forEach(region => params.append('regions', region));
      debouncedFilters.excludedRegions.forEach(region => params.append('excludedRegions', region));
      if (debouncedFilters.timePeriods) {
        params.set('timePeriods', debouncedFilters.timePeriods.join(','));
      }
      
      const url = `/api/events?${params.toString()}`;
      const response = await fetch(url);
      if (!response.ok) {
        reportApiError(url, response.status);
        throw new Error('Failed to fetch events');
      }
      return response.json() as Promise<{ events: Event[]; total: number }>;
    },
    getNextPageParam: (lastPage, allPages) => {
      const loadedCount = allPages.reduce((sum, page) => sum + page.events.length, 0);
      return loadedCount < lastPage.total ? loadedCount : undefined;
    },
    initialPageParam: 0,
    staleTime: 5 * 60 * 1000, // 5 minutes - data refreshes when user returns after being away
    refetchOnWindowFocus: true, // Refresh events when user returns to tab
    placeholderData: (previousData) => previousData,
  });
  
  // Flatten all pages into a single array
  const allEvents = eventsData?.pages?.flatMap(page => page.events) ?? [];
  
  // Get server-provided total (filtered count from entire dataset, not just loaded pages)
  const serverFilteredTotal = eventsData?.pages?.[0]?.total;

  // Track search and filter changes for analytics (debounced, fire-and-forget)
  // Note: Tracking is deferred until we have accurate result counts
  // - For text searches: wait for searchResults from /api/search
  // - For filters only: wait for serverFilteredTotal from /api/events

  // Fetch featured events
  const { data: featuredEvents = [] } = useQuery<Event[]>({
    queryKey: ['/api/events/featured'],
  });

  // Search results
  const { data: searchResults, isLoading: searchLoading } = useQuery<{ events: Event[], query: string }>({
    queryKey: ['/api/search', searchQuery],
    enabled: !!searchQuery && searchQuery.length >= 2,
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
    queryFn: async () => {
      const response = await fetch(`/api/search?q=${encodeURIComponent(searchQuery!)}`);
      if (!response.ok) {
        reportApiError('/api/search', response.status);
        throw new Error('Search failed');
      }
      return response.json();
    }
  });
  
  // Track text searches for analytics (debounced, fire-and-forget)
  // Note: Filter tracking is handled by the onFiltersApply callback
  const trackingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastTrackedSearchRef = useRef<string>('');
  const effectiveTrackingRegions = useMemo(() => {
    if (activeRegionOverride) return [activeRegionOverride];
    if (!routeRegionName) return selectedRegions;
    return selectedRegions.includes(routeRegionName)
      ? selectedRegions
      : [routeRegionName, ...selectedRegions];
  }, [activeRegionOverride, routeRegionName, selectedRegions]);
  const buildTrackedSearchFilters = useCallback((overrides: Partial<SearchFilters> = {}): SearchFilters => {
    const categories = overrides.categories ?? selectedCategories;
    const excludedCategoriesValue = overrides.excludedCategories ?? excludedCategories;
    const sourcesValue = overrides.sources ?? selectedSources;
    const excludedSourcesValue = overrides.excludedSources ?? excludedSources;
    const regionsValue = overrides.regions ?? effectiveTrackingRegions;
    const excludedRegionsValue = overrides.excludedRegions ?? excludedRegions;
    const dateFilterValue = overrides.dateFilter !== undefined ? overrides.dateFilter : dateFilter;
    const customDateValue = overrides.customDate !== undefined ? overrides.customDate : customDate;
    const timePeriodsValue = overrides.timePeriods ?? timePeriods;

    const filters: SearchFilters = {};
    if (categories.length) filters.categories = categories;
    if (excludedCategoriesValue.length) filters.excludedCategories = excludedCategoriesValue;
    if (sourcesValue.length) filters.sources = sourcesValue;
    if (excludedSourcesValue.length) filters.excludedSources = excludedSourcesValue;
    if (regionsValue.length) filters.regions = regionsValue;
    if (excludedRegionsValue.length) filters.excludedRegions = excludedRegionsValue;
    if (dateFilterValue) filters.dateFilter = dateFilterValue;
    if (customDateValue) filters.customDate = customDateValue;
    if (timePeriodsValue.length && !(timePeriodsValue.length === 1 && timePeriodsValue[0] === 'allDay')) {
      filters.timePeriods = timePeriodsValue;
    }

    return filters;
  }, [selectedCategories, excludedCategories, selectedSources, excludedSources, effectiveTrackingRegions, excludedRegions, dateFilter, customDate, timePeriods]);
  // Filter changes are recorded once the list for the new filters has loaded, so the
  // record carries the real result count (at click time the list still shows the old one).
  const pendingFilterTrackRef = useRef<{ term: string | null; filters: SearchFilters; fromSignature: string } | null>(null);
  const flushPendingFilterTrack = useCallback((resultsCount?: number) => {
    const pending = pendingFilterTrackRef.current;
    if (!pending) return;
    pendingFilterTrackRef.current = null;
    trackSearch(pending.term, pending.filters, resultsCount, 'filter_apply');
  }, []);
  const trackCommittedFilterUsage = useCallback((overrides: Partial<SearchFilters> = {}) => {
    // An earlier change whose results never loaded is still recorded, without a count
    flushPendingFilterTrack();
    pendingFilterTrackRef.current = {
      term: searchQuery,
      filters: buildTrackedSearchFilters(overrides),
      fromSignature: filterSignature,
    };
  }, [buildTrackedSearchFilters, filterSignature, flushPendingFilterTrack, searchQuery]);
  useEffect(() => () => flushPendingFilterTrack(), [flushPendingFilterTrack]);
  
  useEffect(() => {
    // Only track text searches here - filter tracking is done on Apply button click
    if (!searchQuery) return;
    
    // Wait for search results to load
    if (searchResults === undefined) return;
    
    // Create a signature to avoid duplicate tracking of the same search state
    const signature = (searchQuery || '').trim().toLowerCase();
    if (lastTrackedSearchRef.current === signature) return;
    
    // Record the search only once the visitor has stopped typing for a moment, so
    // half-typed words ("Dat" on the way to "Dating") aren't logged as searches
    if (trackingTimeoutRef.current) {
      clearTimeout(trackingTimeoutRef.current);
    }

    trackingTimeoutRef.current = setTimeout(() => {
      lastTrackedSearchRef.current = signature;
      trackSearch(searchQuery, buildTrackedSearchFilters(), searchResults.events.length, 'text_input');
      trackEvent("search", { search_term: searchQuery.trim(), result_count: searchResults.events.length });
    }, 2000);
    
    return () => {
      if (trackingTimeoutRef.current) {
        clearTimeout(trackingTimeoutRef.current);
      }
    };
  }, [searchQuery, searchResults, buildTrackedSearchFilters]);
  
  // Track filter usage when Apply button is clicked
  // Captures filter state at click time and tracks immediately (fire-and-forget)
  const handleFiltersApply = useCallback(() => {
    // Only track if there are active filters
    if (!hasActiveFilters) {
      return;
    }
    
    // Track immediately with current filter state
    // Note: We track with the current serverFilteredTotal which reflects the filtered results
    // shown to the user at the time they click Apply
    trackSearch(searchQuery, {
      categories: selectedCategories,
      excludedCategories,
      sources: selectedSources,
      excludedSources,
      regions: effectiveTrackingRegions,
      excludedRegions,
      dateFilter,
      customDate,
      timePeriods,
    }, serverFilteredTotal, 'filter_apply');
  }, [hasActiveFilters, searchQuery, selectedCategories, excludedCategories, selectedSources, excludedSources, effectiveTrackingRegions, excludedRegions, dateFilter, customDate, timePeriods, serverFilteredTotal]);

  // Mutation to toggle event featured status
  const toggleFeaturedMutation = useMutation({
    mutationFn: async ({ id, featured }: { id: string; featured: boolean }) => {
      return await apiRequest('PATCH', `/api/events/${id}/featured`, { featured });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/events'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['/api/events/featured'] });
      queryClient.invalidateQueries({ queryKey: ['/api/search'], exact: false });
      toast({
        title: "Success",
        description: "Event featured status updated",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to update event",
        variant: "destructive",
      });
    },
  });

  // Mutation to delete event
  const deleteEventMutation = useMutation({
    mutationFn: async (id: string) => {
      return await apiRequest('DELETE', `/api/events/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/events'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['/api/events/featured'] });
      queryClient.invalidateQueries({ queryKey: ['/api/search'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['/api/categories'] });
      toast({
        title: "Success",
        description: "Event deleted successfully",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to delete event",
        variant: "destructive",
      });
    },
  });

  // Fetch user's larked event IDs (only when authenticated)
  const { data: larkedEventIds = [] } = useQuery<string[]>({
    queryKey: ['/api/larks/ids'],
    enabled: isAuthenticated,
  });
  
  const larkedEventSet = useMemo(() => new Set(larkedEventIds), [larkedEventIds]);

  // Mutation to toggle lark status
  const toggleLarkMutation = useMutation({
    mutationFn: async ({ eventId, isLarked }: { eventId: string; isLarked: boolean }) => {
      if (isLarked) {
        return await apiRequest('DELETE', `/api/larks/${eventId}`);
      } else {
        return await apiRequest('POST', '/api/larks', { eventId });
      }
    },
    onSuccess: (_, { isLarked }) => {
      queryClient.invalidateQueries({ queryKey: ['/api/larks/ids'] });
      queryClient.invalidateQueries({ queryKey: ['/api/larks'] });
      toast({
        title: isLarked ? "Removed from saved" : "Event saved",
        description: isLarked ? "Event removed from your list" : "Added to your saved events",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Something went wrong. Please try again.",
        variant: "destructive",
      });
    },
  });

  // Infinite scroll: Load more events when user is approaching the end of loaded events
  // Uses rootMargin to start loading 800px before the sentinel comes into view
  useEffect(() => {
    if (!loadMoreRef.current || !hasNextPage || isFetchingNextPage) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (first.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { 
        threshold: 0.1,
        // Start loading 800px before the sentinel enters the viewport
        // This creates a smoother infinite scroll experience
        rootMargin: '0px 0px 800px 0px'
      }
    );

    observer.observe(loadMoreRef.current);
    
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Fetch distinct sources for filtering
  const { data: sources = [] } = useQuery<string[]>({
    queryKey: ['/api/sources'],
  });

  // Recently Viewed — fetch once on mount and keep fresh for 30s
  const sessionId = getSessionId();
  const { data: recentlyViewedEvents = [] } = useQuery<Array<{
    title: string | null;
    venue: string | null;
    date: string | null;
    time: string | null;
    eventDate: string | null;
    category: string | null;
    sourceUrl: string | null;
    source: string | null;
  }>>({
    queryKey: ['/api/recently-viewed', sessionId],
    queryFn: () => fetch(`/api/recently-viewed?sessionId=${encodeURIComponent(sessionId)}`).then(r => r.json()),
    staleTime: 30_000,
  });
  const recentlyViewedCount = recentlyViewedEvents.length;

  const clearRecentlyViewedMutation = useMutation({
    mutationFn: () => apiRequest('DELETE', `/api/recently-viewed?sessionId=${encodeURIComponent(sessionId)}`),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: ['/api/recently-viewed', sessionId] });
      const previous = queryClient.getQueryData(['/api/recently-viewed', sessionId]);
      queryClient.setQueryData(['/api/recently-viewed', sessionId], []);
      return { previous };
    },
    onError: (_err, _vars, context: any) => {
      if (context?.previous) {
        queryClient.setQueryData(['/api/recently-viewed', sessionId], context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/recently-viewed', sessionId] });
    },
  });

  // Create a map from raw category to master category data (name, icon, color)
  const rawCategoryToMasterData = useMemo(() => {
    const map = new Map<string, { name: string; icon: string | null; color: string | null }>();
    categoryFilters.forEach(cf => {
      cf.rawCategories.forEach(rawCat => {
        map.set(rawCat, {
          name: cf.masterCategory.name.trim(),
          icon: cf.masterCategory.icon || null,
          color: cf.masterCategory.color || null,
        });
      });
    });
    return map;
  }, [categoryFilters]);

  // Map master category names to their colors for filter chips
  const masterCategoryColorMap = useMemo(() => {
    const map = new Map<string, string | null>();
    categoryFilters.forEach(cf => {
      map.set(cf.masterCategory.name.trim(), cf.masterCategory.color || null);
    });
    return map;
  }, [categoryFilters]);

  const homepageRegionLinks = useMemo(
    () => HOMEPAGE_REGIONS.filter((region) => allRegionNames.includes(region)),
    [allRegionNames]
  );

  const homepageCategoryLinks = useMemo(
    () => masterCategoryNames,
    [masterCategoryNames]
  );

  const heroRegionPills = useMemo(
    () =>
      homepageRegionLinks.map((region) => ({
        name: region,
        active:
          activeRegionOverride === region ||
          selectedRegions.includes(region) ||
          (excludedRegions.length > 0 && !excludedRegions.includes(region)),
      })),
    [activeRegionOverride, excludedRegions, homepageRegionLinks, selectedRegions]
  );

  const heroCategoryPills = useMemo(
    () =>
      homepageCategoryLinks.map((category) => ({
        name: category,
        active:
          selectedCategories.includes(category) ||
          (excludedCategories.length > 0 && !excludedCategories.includes(category)),
      })),
    [excludedCategories, homepageCategoryLinks, selectedCategories]
  );

  const handleSearch = (query: string) => {
    // Header already pushes to URL, no need to do anything here
  };

  const handleFilterChange = (categories: string[]) => {
    setSelectedCategories(categories);
  };

  const handleExcludeFilterChange = (categories: string[]) => {
    setExcludedCategories(categories);
  };

  const handleSourceFilterChange = (sources: string[]) => {
    setSelectedSourceForUrl(null);
    setSelectedSources(sources);
    setExcludedSources(prev => prev.filter(s => !sources.includes(s)));
  };

  const normalizeRegionSelection = useCallback((regions: string[]) => {
    if (!routeRegionName) {
      return [...new Set(regions)];
    }

    const withoutBaseRegion = regions.filter(region => region !== routeRegionName);
    return [routeRegionName, ...withoutBaseRegion];
  }, [routeRegionName]);

  const handleRegionFilterChange = (regions: string[]) => {
    setSelectedRegions(normalizeRegionSelection(regions));
  };

  const handleDateFilterChange = (filter: DateFilterType, newCustomDate: string | null) => {
    setDateFilter(filter);
    setCustomDate(newCustomDate);
  };

  const handleTimePeriodChange = (periods: TimePeriod[]) => {
    setTimePeriods(periods);
  };

  // Discovery-bar shortcuts: Today, Tonight (today + evening), This Weekend
  const activeQuickDate = getActiveQuickDate(dateFilter, timePeriods);
  const handleQuickDateChange = (next: QuickDate | null) => {
    const nextState = applyQuickDate({ dateFilter, timePeriods }, next);
    setDateFilter(nextState.dateFilter);
    setCustomDate(null);
    setTimePeriods(nextState.timePeriods);
  };

  const handleRegionPillClick = useCallback((region: string) => {
    const nextSelectedRegions = normalizeRegionSelection([region]);
    setActiveRegionOverride(null);
    setSelectedRegions(nextSelectedRegions);
    setExcludedRegions([]);
    trackCommittedFilterUsage({
      regions: nextSelectedRegions,
      excludedRegions: [],
    });
    if (window.innerWidth >= 1024) {
      setSidebarExpandSection({ section: 'regions', item: region, ts: Date.now() });
    }
  }, [normalizeRegionSelection, trackCommittedFilterUsage]);

  const handleHeroRegionNavigation = useCallback((region: string) => {
    setActiveRegionOverride(null);
    setExcludedRegions([]);
    setSelectedRegions([]);
    setSelectedSourceForUrl(null);
    trackCommittedFilterUsage({
      sources: [],
      excludedSources: [],
      regions: [region],
      excludedRegions: [],
    });
    setLocation(getRegionPathFromName(region));
  }, [setLocation, trackCommittedFilterUsage]);

  const handleAllRegionsNavigation = useCallback(() => {
    setActiveRegionOverride(null);
    setExcludedRegions([]);
    setSelectedRegions([]);
    setSelectedSourceForUrl(null);
    setLocation('/');
  }, [setLocation]);

  // Quick-select region override handlers
  const QUICK_SELECT_CITIES = ['Albany', 'Troy', 'Schenectady', 'Saratoga'] as const;

  const handleCityPillClick = useCallback((city: string) => {
    if (allRegionNames.length === 0) return;
    if (activeRegionOverride === city) {
      setActiveRegionOverride(null);
      setExcludedRegions([]);
      setSelectedRegions([]);
      trackCommittedFilterUsage({
        regions: routeRegionName ? [routeRegionName] : [],
        excludedRegions: [],
      });
    } else {
      setActiveRegionOverride(city);
      setSelectedRegions([city]);
      setExcludedRegions([]);
      trackCommittedFilterUsage({
        regions: [city],
        excludedRegions: [],
      });
    }
  }, [activeRegionOverride, allRegionNames, routeRegionName, trackCommittedFilterUsage]);

  const handleClearRegionOverride = useCallback(() => {
    setActiveRegionOverride(null);
    setExcludedRegions([]);
    setSelectedRegions(routeRegionName ? [routeRegionName] : []);
    trackCommittedFilterUsage({
      regions: routeRegionName ? [routeRegionName] : [],
      excludedRegions: [],
    });
  }, [routeRegionName, trackCommittedFilterUsage]);

  // Town buttons in the discovery bar. A button is on when that town is the only
  // region filter; on a town's own page (/regions/troy) they move between town pages instead.
  const isOnlyRegion = useCallback(
    (region: string) =>
      activeRegionOverride === region ||
      (selectedRegions.length === 1 && selectedRegions[0] === region && excludedRegions.length === 0),
    [activeRegionOverride, excludedRegions.length, selectedRegions],
  );
  const regionShortcuts = useMemo(
    () => HOMEPAGE_REGIONS.map((region) => ({
      name: region,
      active: routeRegionName ? routeRegionName === region : isOnlyRegion(region),
    })),
    [isOnlyRegion, routeRegionName],
  );
  const handleRegionShortcut = useCallback((region: string) => {
    if (routeRegionName) {
      if (routeRegionName === region) handleAllRegionsNavigation();
      else handleHeroRegionNavigation(region);
      return;
    }
    if (isOnlyRegion(region)) handleClearRegionOverride();
    else handleCityPillClick(region);
  }, [handleAllRegionsNavigation, handleCityPillClick, handleClearRegionOverride, handleHeroRegionNavigation, isOnlyRegion, routeRegionName]);


  const handleSourceTextClick = useCallback((sourceName: string) => {
    setSelectedSourceForUrl(null);
    setSelectedSources([sourceName]);
    setExcludedSources([]);
    trackCommittedFilterUsage({
      sources: [sourceName],
      excludedSources: [],
    });
    if (window.innerWidth >= 1024) {
      setSidebarExpandSection({ section: 'sources', item: sourceName, ts: Date.now() });
    }
  }, [trackCommittedFilterUsage]);

  const handleCategoryPillClick = useCallback((categoryName: string) => {
    setActiveRegionOverride(null);
    setSearchQuery(null);
    setSelectedCategories([categoryName]);
    setExcludedCategories([]);
    setSelectedSources([]);
    setExcludedSources([]);
    setSelectedRegions([]);
    setExcludedRegions([]);
    setSelectedSourceForUrl(null);
    setDateFilter(null);
    setCustomDate(null);
    setTimePeriods(['allDay']);
    trackCommittedFilterUsage({
      categories: [categoryName],
      excludedCategories: [],
    });
    setLocation(getCategoryPathFromName(categoryName));
  }, [setLocation, trackCommittedFilterUsage]);

  const handleHeroCategoryPillClick = useCallback((categoryName: string) => {
    if (isPureCategoryLandingPage && routeCategoryName === categoryName) {
      setSelectedCategories([]);
      setExcludedCategories([]);
      trackCommittedFilterUsage({
        categories: [],
        excludedCategories: [],
      });
      setLocation('/');
      return;
    }

    handleCategoryPillClick(categoryName);
  }, [handleCategoryPillClick, isPureCategoryLandingPage, routeCategoryName, setLocation, trackCommittedFilterUsage]);

  const beginMobileBrowseSelection = useCallback((selection: PendingMobileBrowseSelection) => {
    try {
      window.sessionStorage.setItem(PENDING_MOBILE_BROWSE_KEY, JSON.stringify(selection));
    } catch {
      // The in-memory handoff still works when browser storage is unavailable.
    }

    setPendingMobileBrowseSelection(selection);
    setBrowseAnnouncement(`${selection.label} selected. Updating events.`);
    setPersistedHeroCollapsed(true);
    setGuidePanel(null);
    setActiveRegionOverride(null);
    setSearchQuery(null);
    setSelectedCategories(selection.kind === 'category' ? [selection.label] : []);
    setExcludedCategories([]);
    setSelectedSources([]);
    setExcludedSources([]);
    setSelectedRegions(selection.kind === 'region' ? [selection.label] : []);
    setExcludedRegions([]);
    setSelectedSourceForUrl(null);
    setDateFilter(null);
    setCustomDate(null);
    setTimePeriods(['allDay']);
    trackCommittedFilterUsage(selection.kind === 'region'
      ? { regions: [selection.label], excludedRegions: [] }
      : { categories: [selection.label], excludedCategories: [] });
    setLocation(selection.path);
  }, [setLocation, setPersistedHeroCollapsed, trackCommittedFilterUsage]);

  const handleMobileBrowseRegionSelect = useCallback((region: string) => {
    beginMobileBrowseSelection({
      kind: 'region',
      label: region,
      path: getRegionPathFromName(region),
    });
  }, [beginMobileBrowseSelection]);

  const handleMobileBrowseCategorySelect = useCallback((category: string) => {
    beginMobileBrowseSelection({
      kind: 'category',
      label: category,
      path: getCategoryPathFromName(category),
    });
  }, [beginMobileBrowseSelection]);

  const handleDateTextClick = useCallback((isoDate: string) => {
    setDateFilter('custom');
    setCustomDate(isoDate);
    trackCommittedFilterUsage({
      dateFilter: 'custom',
      customDate: isoDate,
    });
  }, [trackCommittedFilterUsage]);

  const handleFeaturedEventRemoval = useCallback((eventId: string) => {
    toggleFeaturedMutation.mutate({ id: eventId, featured: false });
  }, [toggleFeaturedMutation]);

  const handleFeaturedEventAdd = useCallback((eventId: string) => {
    toggleFeaturedMutation.mutate({ id: eventId, featured: true });
  }, [toggleFeaturedMutation]);

  const handleEventDelete = useCallback((eventId: string) => {
    deleteEventMutation.mutate(eventId);
  }, [deleteEventMutation]);

  const handleEventLarkToggle = useCallback((eventId: string, isLarked: boolean) => {
    toggleLarkMutation.mutate({ eventId, isLarked });
  }, [toggleLarkMutation]);

  const handleOpenEventDrawer = useCallback((event: EventDrawerEvent) => {
    setSelectedDrawerEvent(event);
  }, []);

  const handleCloseEventDrawer = useCallback(() => {
    setSelectedDrawerEvent(null);
  }, []);

  // Weekend guide picks: events the guide names, resolved by the server (see server/guideEventLinks.ts)
  const { data: guideSummaries = [] } = useQuery<GuideSummary[]>({
    queryKey: ["/api/newsletter-summaries"],
    staleTime: 5 * 60 * 1000,
  });
  const weekendPicks = useMemo(() => {
    const weekend = guideSummaries.find((summary) => summary.type === "weekend");
    return selectGuidePicks((weekend?.eventLinks ?? []).map((link) => link.event));
  }, [guideSummaries]);
  // Event cards tag the events either guide picked
  const guidePickMap = useMemo(() => buildGuidePickMap(guideSummaries), [guideSummaries]);
  // Only on the general list: a search, filter, or town/category page gets unrelated picks otherwise
  const showGuidePicks =
    weekendPicks.length >= 3 && !searchQuery && !hasActiveFilters && !isPureRegionLandingPage && !isPureCategoryLandingPage;

  const handleOpenGuideEvent = useCallback((event: GuideLinkedEvent, position: number, surface: "picks_row" | "guide_panel") => {
    trackEvent("guide_event_open", { surface, position });
    handleOpenEventDrawer({ ...event, clickContext: "guide_pick" });
  }, [handleOpenEventDrawer]);
  const handleOpenGuidePanelEvent = useCallback(
    (event: GuideLinkedEvent, position: number) => handleOpenGuideEvent(event, position, "guide_panel"),
    [handleOpenGuideEvent],
  );

  // Shown once after the first row of events: the weekend guide's picks (or, on phones, links to the
  // guides, which are not in the discovery bar there) and the first-visit onboarding note on desktop.
  const eventListInterlude = useMemo(() => ({
    afterCount: 3,
    content: (
      <>
        {showGuidePicks ? (
          <GuidePicksRow
            picks={weekendPicks}
            onOpenEvent={(event, position) => handleOpenGuideEvent(event, position, "picks_row")}
            onOpenGuide={handleGuideButton}
          />
        ) : (
          <GuideShortcuts onOpenGuide={handleGuideButton} />
        )}
        <Suspense fallback={null}>
          <OnboardingToast variant="inline" />
        </Suspense>
      </>
    ),
  }), [handleGuideButton, handleOpenGuideEvent, showGuidePicks, weekendPicks]);

  const handleFeaturedInteraction = useCallback((interaction: {
    eventType: FeaturedInteractionType;
    selectedIndex: number;
    targetIndex?: number | null;
    interactionSource: 'prev_button' | 'next_button' | 'dot';
  }) => {
    const sourceIndex = interaction.targetIndex ?? interaction.selectedIndex;
    const featuredEvent = featuredEvents[sourceIndex];

    if (!featuredEvent) {
      return;
    }

    trackFeaturedInteraction({
      sessionId: getSessionId(),
      eventType: interaction.eventType,
      eventId: featuredEvent.id,
      eventSlug: featuredEvent.slug ?? null,
      title: featuredEvent.title,
      venue: featuredEvent.venue,
      source: featuredEvent.source ?? null,
      sourceUrl: featuredEvent.sourceUrl ?? null,
      category: featuredEvent.category ?? null,
      region: featuredEvent.region ?? null,
      position: sourceIndex,
      selectedIndex: interaction.selectedIndex,
      targetIndex: interaction.targetIndex ?? null,
      featuredCount: featuredEvents.length,
      interactionSource: interaction.interactionSource,
    });
  }, [featuredEvents]);

  const featuredEventCards = useMemo(
    () =>
      featuredEvents.map((event, index) => {
        const masterData = event.category ? rawCategoryToMasterData.get(event.category) : undefined;
        const isLarked = larkedEventSet.has(event.id);
        return (
          <CompactEventItem
            key={event.id}
            {...event}
            isFeatured={true}
            showFeaturedBadge={false}
            showDetailsChevron={false}
            masterCategoryName={masterData?.name}
            masterCategoryIcon={masterData?.icon}
            masterCategoryColor={masterData?.color}
            isAdmin={isAdmin}
            isAuthenticated={isAuthenticated}
            isLarked={isLarked}
            onToggleFeatured={() => handleFeaturedEventRemoval(event.id)}
            onDelete={() => handleEventDelete(event.id)}
            onToggleLark={() => handleEventLarkToggle(event.id, isLarked)}
            onRegionClick={handleRegionPillClick}
            onSourceClick={handleSourceTextClick}
            onCategoryClick={handleCategoryPillClick}
            onDateClick={handleDateTextClick}
            clickContext={clickContextValue}
            queryTerm={searchQuery}
            filtersJson={filtersJsonValue}
            discoveryCollapsed={heroCollapsed}
            onOpenEventDrawer={handleOpenEventDrawer}
            userAccountsEnabled={userAccountsEnabled}
            onFeaturedCardClick={() => {
              trackFeaturedInteraction({
                sessionId: getSessionId(),
                eventType: 'featured_card_click',
                eventId: event.id,
                eventSlug: event.slug ?? null,
                title: event.title,
                venue: event.venue,
                source: event.source ?? null,
                sourceUrl: event.sourceUrl ?? null,
                category: event.category ?? null,
                region: event.region ?? null,
                position: index,
                selectedIndex: index,
                targetIndex: index,
                featuredCount: featuredEvents.length,
                interactionSource: 'card',
              });
            }}
            onFeaturedMoreDetailsClick={() => {
              trackFeaturedInteraction({
                sessionId: getSessionId(),
                eventType: 'featured_more_details_click',
                eventId: event.id,
                eventSlug: event.slug ?? null,
                title: event.title,
                venue: event.venue,
                source: event.source ?? null,
                sourceUrl: event.sourceUrl ?? null,
                category: event.category ?? null,
                region: event.region ?? null,
                position: index,
                selectedIndex: index,
                targetIndex: index,
                featuredCount: featuredEvents.length,
                interactionSource: 'more_details',
              });
            }}
          />
        );
      }),
    [
      featuredEvents,
      rawCategoryToMasterData,
      isAdmin,
      isAuthenticated,
      larkedEventSet,
      handleFeaturedEventRemoval,
      handleEventDelete,
      handleEventLarkToggle,
      handleRegionPillClick,
      handleSourceTextClick,
      handleCategoryPillClick,
      handleDateTextClick,
      handleOpenEventDrawer,
      clickContextValue,
      searchQuery,
      filtersJsonValue,
      heroCollapsed,
    ]
  );

  const clearSearch = () => {
    // Immediately reset search state so count updates right away
    setSearchQuery(null);
    // Remove cached search results to prevent stale data
    queryClient.removeQueries({ queryKey: ['/api/search'] });
    // Navigate to home
    setLocation('/');
  };

  // Memoize display events to avoid recomputing on every render
  const displayEvents = useMemo(() => {
    const searchEventsArray = Array.isArray(searchResults?.events) ? searchResults.events : [];
    let events = searchQuery ? searchEventsArray : allEvents;
    
    // Always suppress events without a category or without a region
    events = events.filter(event => event.category && event.category.trim() !== '');
    events = events.filter(event => (event as any).region && (event as any).region !== 'Unknown');
    
    // Apply client-side filters ONLY to search results (main events are already filtered server-side)
    if (searchQuery) {
      if (selectedCategories.length > 0) {
        const selectedFilters = categoryFilters.filter(cf => 
          selectedCategories.includes(cf.masterCategory.name.trim())
        );
        const allowedRawCategories = new Set(
          selectedFilters.flatMap(cf => cf.rawCategories)
        );
        events = events.filter(event => 
          event.category && allowedRawCategories.has(event.category)
        );
      }

      if (excludedCategories.length > 0) {
        const excludedFilters = categoryFilters.filter(cf => 
          excludedCategories.includes(cf.masterCategory.name.trim())
        );
        const excludedRawCategories = new Set(
          excludedFilters.flatMap(cf => cf.rawCategories)
        );
        events = events.filter(event => 
          !event.category || !excludedRawCategories.has(event.category)
        );
      }

      if (selectedSources.length > 0) {
        events = events.filter(event => 
          event.source && selectedSources.includes(event.source)
        );
      }

      if (excludedSources.length > 0) {
        events = events.filter(event =>
          !event.source || !excludedSources.includes(event.source)
        );
      }

      if (selectedRegions.length > 0) {
        events = events.filter(event => 
          (event as any).region && selectedRegions.includes((event as any).region)
        );
      }

      if (excludedRegions.length > 0) {
        events = events.filter(event =>
          (event as any).region && !excludedRegions.includes((event as any).region)
        );
      }

      if (filterDates.length > 0) {
        events = events.filter(event => {
          if (!event.date) return false;
          try {
            const eventDateStr = extractDateString(event.date);
            return filterDates.includes(eventDateStr);
          } catch {
            return false;
          }
        });
      }

      if (!timePeriods.includes('allDay')) {
        events = filterEventsByTimePeriods(events, timePeriods);
      }
    }

    return events;
  }, [searchQuery, searchResults, allEvents, selectedCategories, excludedCategories, selectedSources, excludedSources, selectedRegions, excludedRegions, filterDates, timePeriods, categoryFilters]);

  // When in search mode, compute counts client-side from displayEvents so the filter panel
  // stays in sync with what the user actually sees (the server /api/filters/counts endpoint
  // has no awareness of the active search query).
  const searchAwareCounts = useMemo<UnifiedFilterCountsResponse | undefined>(() => {
    if (!searchQuery) return undefined;
    const sourceCounts: Record<string, number> = {};
    const categoryCounts: Record<string, number> = {};
    const regionCounts: Record<string, number> = {};
    for (const event of displayEvents) {
      if (event.source) sourceCounts[event.source] = (sourceCounts[event.source] ?? 0) + 1;
      if ((event as any).masterCategoryName) {
        const name = (event as any).masterCategoryName as string;
        categoryCounts[name] = (categoryCounts[name] ?? 0) + 1;
      }
      if ((event as any).region) {
        const region = (event as any).region as string;
        regionCounts[region] = (regionCounts[region] ?? 0) + 1;
      }
    }
    return { sourceCounts, categoryCounts, regionCounts, totalEvents: displayEvents.length };
  }, [searchQuery, displayEvents]);

  // Status bar count flash when value changes
  const statusBarCount = searchQuery ? displayEvents.length : serverFilteredTotal;
  const isStatusCountLoading = searchQuery ? searchLoading : eventsLoading && !eventsData;

  // Send the pending filter-change record once the results for the new filters are showing
  useEffect(() => {
    const pending = pendingFilterTrackRef.current;
    if (!pending || filterSignature === pending.fromSignature) return;
    if (!filtersRestored || debouncedSignature !== filterSignature || eventsLoading || eventsFetching) return;
    if (searchQuery && searchLoading) return;
    flushPendingFilterTrack(statusBarCount ?? undefined);
  }, [debouncedSignature, eventsFetching, eventsLoading, filterSignature, filtersRestored, flushPendingFilterTrack, searchLoading, searchQuery, statusBarCount]);

  const statusBarCountText = isStatusCountLoading || statusBarCount == null ? '…' : statusBarCount.toLocaleString();
  const [statusCountFlashing, setStatusCountFlashing] = useState(false);
  const prevStatusCountRef = useRef<number | null>(null);
  useEffect(() => {
    if (isStatusCountLoading || statusBarCount == null) return;
    if (prevStatusCountRef.current !== null && prevStatusCountRef.current !== statusBarCount) {
      setStatusCountFlashing(true);
      const t = setTimeout(() => setStatusCountFlashing(false), 450);
      return () => clearTimeout(t);
    }
    prevStatusCountRef.current = statusBarCount;
  }, [statusBarCount, isStatusCountLoading]);

  useEffect(() => {
    if (!pendingMobileBrowseSelection) return;
    if (window.location.pathname !== pendingMobileBrowseSelection.path) return;
    if (pendingMobileBrowseSelection.kind === 'region' && routeRegionName !== pendingMobileBrowseSelection.label) return;
    if (pendingMobileBrowseSelection.kind === 'category' && routeCategoryName !== pendingMobileBrowseSelection.label) return;
    if (!filtersRestored || debouncedSignature !== filterSignature || eventsLoading || eventsFetching) return;

    const resultCount = serverFilteredTotal ?? displayEvents.length;
    const resultLabel = `${resultCount.toLocaleString()} ${resultCount === 1 ? 'event' : 'events'}`;
    setBrowseAnnouncement(`${pendingMobileBrowseSelection.label} selected. Showing ${resultLabel}.`);

    let secondFrame = 0;
    const firstFrame = window.requestAnimationFrame(() => {
      secondFrame = window.requestAnimationFrame(() => {
        setPendingMobileBrowseSelection(null);
        clearPendingMobileBrowseSelection();
        scrollToEventResults();
      });
    });

    return () => {
      window.cancelAnimationFrame(firstFrame);
      if (secondFrame) window.cancelAnimationFrame(secondFrame);
    };
  }, [
    debouncedSignature,
    displayEvents.length,
    eventsFetching,
    eventsLoading,
    filterSignature,
    filtersRestored,
    pendingMobileBrowseSelection,
    routeCategoryName,
    routeRegionName,
    serverFilteredTotal,
  ]);

  // Show all events in Latest Events section, including featured ones
  // When the weekend filter is active, exclude Friday events before 3:00 PM
  // When the today filter is active, exclude events whose start hour is before the current hour
  const regularEvents = useMemo(() => {
    let events = displayEvents;

    if (dateFilter === 'weekend') {
      const FRIDAY = 5;
      const THREE_PM_MINUTES = 15 * 60;
      events = events.filter(event => {
        if (!event.date) return true;
        try {
          const eventDateStr = extractDateString(event.date);
          const eventDate = new Date(eventDateStr + 'T00:00:00');
          if (eventDate.getDay() !== FRIDAY) return true;
          const eventMinutes = parseTimeToMinutes(event.time);
          if (eventMinutes === null) return true;
          return eventMinutes >= THREE_PM_MINUTES;
        } catch {
          return true;
        }
      });
    }

    if (dateFilter === 'today') {
      const currentHourThreshold = new Date().getHours() * 60; // TODAY FILTER — hide past-hour events
      events = events.filter(event => {
        const eventMinutes = event.timeSortMinutes ?? parseTimeToMinutes(event.time);
        if (eventMinutes === null || eventMinutes === undefined) return true; // no time = show (fail open)
        return eventMinutes >= currentHourThreshold; // matches server: time_sort_minutes >= currentHour * 60
      });
    }

    return events;
  }, [displayEvents, dateFilter]);
  
  // Group events by date for rendering with sticky headers
  const groupedEvents = useMemo(() => groupEventsByDate(regularEvents), [regularEvents]);

  // "Jump to" bar: the loaded days and the times of day each one has
  const loadedDates = useMemo(() => groupedEvents.map((group) => group.dateString), [groupedEvents]);
  const periodsByDate = useMemo(
    () => new Map<string, DayPeriod[]>(
      groupedEvents.map((group) => [group.dateString, splitByDayPeriod(group.events).map((section) => section.period)]),
    ),
    [groupedEvents],
  );
  // cancelRefetch: false, so a jump and the scroll loader asking at once share one request
  const loadMoreForJump = useCallback(() => fetchNextPage({ cancelRefetch: false }), [fetchNextPage]);

  // Keep the visitor's place in the list across venue/event pages (restored on Back only)
  const listScrollRef = useRef<{ url: string; position: ListScrollPosition }>({
    url: '',
    position: { y: 0, anchorId: null, anchorOffset: 0 },
  });
  const listPathRef = useRef(location);
  listPathRef.current = location;
  const pendingScrollRestoreRef = useRef<ListScrollPosition | null>(
    wasBackForwardNavigation() ? readListScroll(currentListUrl()) : null,
  );
  useLayoutEffect(() => {
    let frame = 0;
    const remember = () => {
      frame = 0;
      // Ignore scrolls after the address has moved to another page (the next page's
      // layout can clamp or reset scroll before this list finishes unmounting)
      const basePath = import.meta.env.BASE_URL.replace(/\/$/, '');
      if (window.location.pathname !== `${basePath}${listPathRef.current}`) return;
      listScrollRef.current = { url: currentListUrl(), position: measureListScroll() };
    };
    const onScroll = () => {
      if (!frame) frame = window.requestAnimationFrame(remember);
    };
    remember();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (frame) window.cancelAnimationFrame(frame);
      saveListScroll(listScrollRef.current.url, listScrollRef.current.position);
    };
  }, []);
  useEffect(() => {
    const position = pendingScrollRestoreRef.current;
    if (position === null || groupedEvents.length === 0) return;
    pendingScrollRestoreRef.current = null;
    // Re-align a few times while late content above the list (notes, picks) settles,
    // unless the visitor starts scrolling first
    let cancelled = false;
    const stop = () => { cancelled = true; };
    const userEvents = ['wheel', 'touchstart', 'keydown', 'mousedown'] as const;
    userEvents.forEach((name) => window.addEventListener(name, stop, { passive: true, once: true }));
    const timers = [0, 150, 400, 800, 1500].map((delay) =>
      window.setTimeout(() => {
        if (!cancelled) applyListScroll(position);
      }, delay),
    );
    return () => {
      timers.forEach((timer) => window.clearTimeout(timer));
      userEvents.forEach((name) => window.removeEventListener(name, stop));
    };
  }, [groupedEvents.length]);

  const visibleStructuredEvents = useMemo(
    () => [...featuredEvents, ...regularEvents].filter((event) => event.slug).slice(0, 10),
    [featuredEvents, regularEvents]
  );

  const landingCollectionName = useMemo(() => {
    if (isPureRegionDateIntentLandingPage && routeRegionName && routeDateIntentConfig) {
      return buildRegionDateIntentMeta(routeRegionName, routeDateIntentConfig).collectionName;
    }

    if (isPureRegionLandingPage && routeRegionName) {
      return `${routeRegionName} Events`;
    }

    if (isPureCategoryLandingPage && routeCategoryName) {
      return `${routeCategoryName} Events`;
    }

    if (isPureDateIntentLandingPage && routeDateIntentConfig) {
      return routeDateIntentConfig.collectionName;
    }

    if (
      !searchQuery &&
      selectedCategories.length === 1 &&
      excludedCategories.length === 0 &&
      selectedSources.length === 0 &&
      excludedSources.length === 0 &&
      selectedRegions.length === 0 &&
      excludedRegions.length === 0 &&
      dateFilter === null &&
      isDefaultTimeFilter
    ) {
      return `${selectedCategories[0]} Events`;
    }

    return "Albany & Capital District Events";
  }, [
    dateFilter,
    excludedCategories.length,
    excludedRegions.length,
    excludedSources.length,
    isDefaultTimeFilter,
    isPureCategoryLandingPage,
    isPureRegionLandingPage,
    isPureDateIntentLandingPage,
    isPureRegionDateIntentLandingPage,
    routeCategoryName,
    routeRegionName,
    routeDateIntentConfig,
    searchQuery,
    selectedCategories,
    selectedRegions.length,
    selectedSources.length,
  ]);

  const landingStructuredData = useMemo(() => {
    if (seoMeta.robots !== "index, follow") {
      return [] as object[];
    }

    const collectionPage = {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: landingCollectionName,
      description: seoMeta.description,
      url: seoMeta.canonical,
      isPartOf: {
        "@type": "WebSite",
        name: "On A Lark",
        url: `${HOME_BASE_URL}/`,
      },
    };

    const itemList =
      visibleStructuredEvents.length > 0
        ? {
            "@context": "https://schema.org",
            "@type": "ItemList",
            name: `${landingCollectionName} Listings`,
            itemListOrder: "https://schema.org/ItemListOrderAscending",
            numberOfItems: visibleStructuredEvents.length,
            itemListElement: visibleStructuredEvents.map((event, index) => ({
              "@type": "ListItem",
              position: index + 1,
              url: `${HOME_BASE_URL}/events/${event.slug}`,
              item: {
                "@type": "Event",
                name: event.title,
                url: `${HOME_BASE_URL}/events/${event.slug}`,
                startDate: event.eventDate ?? event.date,
                location: event.venue
                  ? {
                      "@type": "Place",
                      name: event.venue,
                    }
                  : undefined,
              },
            })),
          }
        : null;

    const schemas: object[] = [collectionPage];

    if (seoMeta.canonical === `${HOME_BASE_URL}/`) {
      schemas.unshift(WEBSITE_SCHEMA, ORGANIZATION_SCHEMA);
    }

    if (seoMeta.canonical !== `${HOME_BASE_URL}/`) {
      schemas.push(buildBreadcrumbSchema([
        { name: "Home", url: `${HOME_BASE_URL}/` },
        { name: landingCollectionName, url: seoMeta.canonical },
      ]));
    }

    if (itemList) {
      schemas.push(itemList);
    }

    return schemas;
  }, [landingCollectionName, seoMeta.canonical, seoMeta.description, seoMeta.robots, visibleStructuredEvents]);

  const landingHeading = useMemo(() => {
    if (isPureRegionDateIntentLandingPage && routeRegionName && routeDateIntentConfig) {
      return buildRegionDateIntentMeta(routeRegionName, routeDateIntentConfig).h1;
    }
    if (isPureRegionLandingPage && routeRegionName) {
      return `${routeRegionName} Events`;
    }
    if (isPureCategoryLandingPage && routeCategoryName) {
      return `${routeCategoryName} Events`;
    }
    if (isPureDateIntentLandingPage && routeDateIntentConfig) {
      return routeDateIntentConfig.h1;
    }
    return HOMEPAGE_H1;
  }, [isPureCategoryLandingPage, isPureRegionLandingPage, isPureDateIntentLandingPage, isPureRegionDateIntentLandingPage, routeCategoryName, routeRegionName, routeDateIntentConfig]);

  const landingIntro = useMemo(() => {
    if (isPureRegionDateIntentLandingPage && routeRegionName && routeDateIntentConfig) {
      return buildRegionDateIntentMeta(routeRegionName, routeDateIntentConfig).intro;
    }
    if (isPureDateIntentLandingPage && routeDateIntentConfig) {
      return routeDateIntentConfig.intro;
    }
    if ((isPureRegionLandingPage && routeRegionName) || (isPureCategoryLandingPage && routeCategoryName)) {
      return seoMeta.description;
    }
    return HOMEPAGE_INTRO;
  }, [isPureCategoryLandingPage, isPureRegionLandingPage, isPureDateIntentLandingPage, isPureRegionDateIntentLandingPage, routeCategoryName, routeRegionName, routeDateIntentConfig, seoMeta.description]);
  
  // Surprise Me functions
  const handleSurpriseMe = () => {
    // Save current scroll position and focused element
    setSavedScrollPosition(window.scrollY);
    setPreviousFocusElement(document.activeElement as HTMLElement);
    
    // Get events within next 7 days (including today)
    const eligibleEvents = filterEventsWithin7Days(displayEvents);
    
    if (eligibleEvents.length === 0) {
      toast({
        title: "No events available",
        description: "No events found within the next 7 days matching your current filters.",
        variant: "destructive",
      });
      return;
    }
    
    const picked = pickRandomEvent(eligibleEvents, lastRandomEventId);
    if (picked) {
      setRandomEvent(picked);
      setLastRandomEventId(picked.id);
      setViewMode('SURPRISE');
      
      // Focus the surprise card heading after render
      setTimeout(() => {
        surpriseCardRef.current?.focus();
      }, 100);
    }
  };
  
  const handleDoItAgain = () => {
    const eligibleEvents = filterEventsWithin7Days(displayEvents);
    const picked = pickRandomEvent(eligibleEvents, lastRandomEventId);
    if (picked) {
      setRandomEvent(picked);
      setLastRandomEventId(picked.id);
    }
  };
  
  const handleLater = () => {
    setViewMode('HOME');
    setRandomEvent(null);
    
    // Restore scroll position and focus
    setTimeout(() => {
      window.scrollTo({ top: savedScrollPosition, behavior: 'smooth' });
      
      // Restore focus to the element that triggered Surprise Me (usually the button)
      if (previousFocusElement && typeof previousFocusElement.focus === 'function') {
        previousFocusElement.focus();
      }
    }, 50);
  };
  
  const clearFilters = () => {
    setSelectedCategories([]);
    setExcludedCategories([]);
    setSelectedSources([]);
    setExcludedSources([]);
    setSelectedRegions(routeRegionName ? [routeRegionName] : []);
    setExcludedRegions([]);
    setActiveRegionOverride(null);
    setDateFilter(null);
    setCustomDate(null);
    setTimePeriods(['allDay']);
    setSelectedSourceForUrl(null);
    localStorage.removeItem(FILTER_STORAGE_KEY);
    setLocation(routeRegionName || routeCategoryName ? '/' : (routeRegionPath ?? routeCategoryPath ?? '/'));
  };

  // Keeps the search term (clearFilters also resets the URL, which drops ?q=)
  const handleSearchWithoutFilters = () => {
    setSelectedCategories([]);
    setExcludedCategories([]);
    setSelectedSources([]);
    setExcludedSources([]);
    setSelectedRegions(routeRegionName ? [routeRegionName] : []);
    setExcludedRegions([]);
    setActiveRegionOverride(null);
    setDateFilter(null);
    setCustomDate(null);
    setTimePeriods(['allDay']);
    setSelectedSourceForUrl(null);
  };

  const openMobileFilterSheet = (tab: 'regions' | 'categories' | 'sources') => {
    setFilterUiState('mobile');
    setMobileExpandSection({ section: tab, ts: Date.now() });
  };

  const openFilterSection = (section: 'regions' | 'categories' | 'sources') => {
    trackFilterUiInteraction({
      interactionType: 'summary_chip_open',
      surface: window.innerWidth >= 1024 ? 'desktop_summary' : 'mobile_summary',
      section,
      label: section.charAt(0).toUpperCase() + section.slice(1),
    });
    if (window.innerWidth >= 1024) {
      setFilterUiState('desktop');
      setSidebarExpandSection({ section, ts: Date.now() });
      return;
    }

    openMobileFilterSheet(section);
  };

  const clearCategorySummaryFilters = useCallback(() => {
    setSelectedCategories([]);
    setExcludedCategories([]);
    trackCommittedFilterUsage({
      categories: [],
      excludedCategories: [],
    });
  }, [trackCommittedFilterUsage]);

  const clearSourceSummaryFilters = useCallback(() => {
    setSelectedSourceForUrl(null);
    setSelectedSources([]);
    setExcludedSources([]);
    trackCommittedFilterUsage({
      sources: [],
      excludedSources: [],
    });
  }, [trackCommittedFilterUsage]);

  const openUnifiedFilters = useCallback(() => {
    const desktop = window.innerWidth >= 1024;
    trackFilterUiInteraction({
      interactionType: 'filters_open',
      surface: desktop ? 'desktop_summary' : 'mobile_summary',
      label: 'Filter events',
    });
    setFilterUiState(desktop ? 'desktop' : 'mobile');
  }, []);

  const dialogFilterDraft: DialogFilterDraft = useMemo(() => ({
    selectedCategories,
    excludedCategories,
    selectedSources,
    excludedSources,
    selectedRegions,
    excludedRegions,
    dateFilter,
    customDate,
    timePeriods,
  }), [
    customDate,
    dateFilter,
    excludedCategories,
    excludedRegions,
    excludedSources,
    selectedCategories,
    selectedRegions,
    selectedSources,
    timePeriods,
  ]);

  const applyDialogFilterDraft = useCallback((draft: DialogFilterDraft) => {
    setSelectedCategories(draft.selectedCategories);
    setExcludedCategories(draft.excludedCategories);
    setSelectedSources(draft.selectedSources);
    setExcludedSources(draft.excludedSources);
    setSelectedRegions(normalizeRegionSelection(draft.selectedRegions));
    setExcludedRegions(draft.excludedRegions);
    setDateFilter(draft.dateFilter);
    setCustomDate(draft.customDate);
    setTimePeriods(draft.timePeriods);
    setSelectedSourceForUrl(null);
    setActiveRegionOverride(null);
    setFilterUiState("closed");
  }, [normalizeRegionSelection]);

  const discoveryFilterChips: DiscoveryFilterChip[] = (() => {
    const chips: DiscoveryFilterChip[] = [];
    const summarize = (values: string[], pluralNoun: string) => {
      if (values.length === 0) return null;
      if (values.length === 1) return values[0];
      return `${values[0]} +${values.length - 1} ${pluralNoun}`;
    };

    if (searchQuery) {
      chips.push({ id: 'search', label: `Search: ${searchQuery}`, onRemove: clearSearch });
    }

    if (dateFilter) {
      const dateLabel = dateFilter === 'today'
        ? 'Today'
        : dateFilter === 'tomorrow'
          ? 'Tomorrow'
          : dateFilter === 'weekend'
            ? 'This Weekend'
            : customDate
              ? new Date(`${customDate}T12:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
              : 'Custom date';
      chips.push({
        id: 'date',
        label: dateLabel,
        onRemove: () => handleDateFilterChange(null, null),
      });
    }

    const visibleRegions = activeRegionOverride
      ? [activeRegionOverride]
      : selectedRegions.length > 0
        ? selectedRegions
        : excludedRegions.length > 0
          ? allRegionNames.filter((region) => !excludedRegions.includes(region))
          : routeRegionName
            ? [routeRegionName]
            : [];
    const regionLabel = summarize(visibleRegions, 'regions');
    if (regionLabel) {
      const routeOnlyRegion = Boolean(
        routeRegionName &&
        !activeRegionOverride &&
        excludedRegions.length === 0 &&
        selectedRegions.every((region) => region === routeRegionName),
      );
      chips.push({
        id: 'region',
        label: regionLabel,
        onRemove: routeOnlyRegion
          ? undefined
          : () => {
              setActiveRegionOverride(null);
              setExcludedRegions([]);
              setSelectedRegions(routeRegionName ? [routeRegionName] : []);
            },
      });
    }

    const visibleCategories = selectedCategories.length > 0
      ? selectedCategories
      : excludedCategories.length > 0
        ? masterCategoryNames.filter((category) => !excludedCategories.includes(category))
        : routeCategoryName
          ? [routeCategoryName]
          : [];
    const categoryLabel = summarize(visibleCategories, 'categories');
    if (categoryLabel) {
      const routeOnlyCategory = Boolean(
        routeCategoryName && selectedCategories.length === 0 && excludedCategories.length === 0,
      );
      chips.push({
        id: 'category',
        label: categoryLabel,
        onRemove: routeOnlyCategory ? undefined : clearCategorySummaryFilters,
      });
    }

    const visibleSources = selectedSources.length > 0
      ? selectedSources
      : excludedSources.length > 0
        ? sources.filter((source) => !excludedSources.includes(source))
        : [];
    const sourceLabel = summarize(visibleSources, 'calendars');
    if (sourceLabel) {
      chips.push({ id: 'source', label: sourceLabel, onRemove: clearSourceSummaryFilters });
    }

    // Every chosen time of day, in day order: "Afternoon & Evening"
    const selectedTimes = (['morning', 'afternoon', 'evening'] as const).filter((period) => timePeriods.includes(period));
    if (selectedTimes.length > 0) {
      const labels = selectedTimes.map((period) => (
        period === 'morning' ? 'Morning' : period === 'afternoon' ? 'Afternoon' : 'Evening'
      ));
      chips.push({
        id: 'time',
        label: labels.length > 1 ? `${labels.slice(0, -1).join(', ')} & ${labels[labels.length - 1]}` : labels[0],
        onRemove: () => setTimePeriods(['allDay']),
      });
    }

    return chips;
  })();

  // The end of a complete list names the active filters, so a short list isn't taken for the
  // whole calendar. After a change there, the start of the new list is brought into view.
  const listEndNote = (
    <ListEndNote
      chips={discoveryFilterChips}
      onShowAll={() => {
        clearSearch();
        clearFilters();
      }}
      onFiltersChange={() => {
        window.requestAnimationFrame(() => window.requestAnimationFrame(scrollToEventResults));
      }}
    />
  );

  const discoveryActiveFilterCount = useMemo(() => {
    const hasRegion = Boolean(
      activeRegionOverride ||
      excludedRegions.length > 0 ||
      selectedRegions.some((region) => region !== routeRegionName),
    );
    const hasCategory = Boolean(
      excludedCategories.length > 0 ||
      selectedCategories.length > 0,
    );
    const hasSource = excludedSources.length > 0 || selectedSources.length > 0;
    // Tonight is one choice (today + evening), so its time part is not counted separately
    const hasTime = timePeriods.some((period) => period !== 'allDay') &&
      getActiveQuickDate(dateFilter, timePeriods) !== 'tonight';
    return Number(Boolean(dateFilter)) + Number(hasRegion) + Number(hasCategory) + Number(hasSource) + Number(hasTime);
  }, [
    activeRegionOverride,
    dateFilter,
    excludedCategories.length,
    excludedRegions.length,
    excludedSources.length,
    routeRegionName,
    selectedCategories.length,
    selectedRegions,
    selectedSources.length,
    timePeriods,
  ]);

  const handleExcludeSourceChange = (source: string) => {
    setSelectedSourceForUrl(null);
    setExcludedSources(prev => {
      if (prev.includes(source)) {
        return prev.filter(s => s !== source);
      } else {
        setSelectedSources(sp => sp.filter(s => s !== source));
        return [...prev, source];
      }
    });
  };

  const handleBulkExcludeSourcesChange = (sources: string[]) => {
    setSelectedSourceForUrl(null);
    setExcludedSources(sources);
    if (sources.length === 0) {
      setSelectedSources([]);
    }
  };

  // Sidebar toggle helpers (same pattern as Header.tsx internally)
  const sidebarToggleCategory = (cat: string) => {
    const updated = selectedCategories.includes(cat)
      ? selectedCategories.filter(c => c !== cat)
      : [...selectedCategories, cat];
    handleFilterChange(updated);
  };

  const sidebarToggleExcludedCategory = (cat: string) => {
    setExcludedCategories(prev =>
      prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat]
    );
  };

  const sidebarToggleSource = (src: string) => {
    const updated = selectedSources.includes(src)
      ? selectedSources.filter(s => s !== src)
      : [...selectedSources, src];
    handleSourceFilterChange(updated);
  };

  const sidebarToggleRegion = (region: string) => {
    const updated = selectedRegions.includes(region)
      ? selectedRegions.filter(r => r !== region)
      : [...selectedRegions, region];
    handleRegionFilterChange(updated);
  };

  const sidebarToggleExcludedRegion = (region: string) => {
    if (activeRegionOverride !== null) {
      setActiveRegionOverride(null);
    }
    setExcludedRegions(prev =>
      prev.includes(region) ? prev.filter(r => r !== region) : [...prev, region]
    );
  };

  const sidebarHandleDateFilterChange = (filter: DateFilterType, customDateVal?: string) => {
    handleDateFilterChange(filter, customDateVal || null);
  };

  if (routeRegionSlug && !regionsLoading && !routeRegionName) {
    return <NotFound />;
  }

  if (routeCategorySlug && !categoriesLoading && !routeCategoryName) {
    return <NotFound />;
  }

  if (routeRegionSlug && regionsLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <p className="text-muted-foreground">Loading region page...</p>
      </div>
    );
  }

  if (routeCategorySlug && categoriesLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <p className="text-muted-foreground">Loading category page...</p>
      </div>
    );
  }


  if (!filtersRestored || eventsLoading) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Helmet>
          <title>{seoMeta.title}</title>
          <meta name="description" content={seoMeta.description} />
          <meta name="robots" content={seoMeta.robots} />
          <link rel="canonical" href={seoMeta.canonical} />
          <meta property="og:title" content={seoMeta.title} />
          <meta property="og:description" content={seoMeta.description} />
          <meta property="og:url" content={seoMeta.canonical} />
          <meta property="og:type" content="website" />
          <meta property="og:site_name" content="On A Lark" />
          <meta property="og:locale" content="en_US" />
          <meta property="og:image" content={seoMeta.image ?? SHARE_IMAGE_URL} />
          <meta property="og:image:alt" content={seoMeta.title} />
          <meta name="twitter:title" content={seoMeta.title} />
          <meta name="twitter:description" content={seoMeta.description} />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:image" content={seoMeta.image ?? SHARE_IMAGE_URL} />
          <meta name="twitter:image:alt" content={seoMeta.title} />
          {landingStructuredData.map((schema, index) => (
            <script key={`home-structured-loading-${index}`} type="application/ld+json">
              {JSON.stringify(schema)}
            </script>
          ))}
        </Helmet>
        <Header 
          categories={masterCategoryNames}
          categoryFilters={categoryFilters}
          sources={sources}
          selectedCategories={selectedCategories}
          excludedCategories={excludedCategories}
          selectedSources={selectedSources}
          excludedSources={excludedSources}
          selectedRegions={selectedRegions}
          excludedRegions={excludedRegions}
          searchValue={searchQuery}
          onSearch={handleSearch} 
          onFilterChange={handleFilterChange}
          onExcludeFilterChange={handleExcludeFilterChange}
          onSourceFilterChange={handleSourceFilterChange}
          onExcludeSourceToggle={handleExcludeSourceChange}
          onBulkExcludeSourcesChange={handleBulkExcludeSourcesChange}
          onRegionFilterChange={handleRegionFilterChange}
          onExcludeRegionToggle={sidebarToggleExcludedRegion}
          onDateFilterChange={handleDateFilterChange}
          timePeriods={timePeriods}
          onTimePeriodChange={handleTimePeriodChange}
          dateFilter={dateFilter}
          customDate={customDate}
          onSurpriseMe={handleSurpriseMe}
          surpriseMeDisabled={eventsLoading || filterEventsWithin7Days(displayEvents).length === 0 || activeFilterCount > 0}
          exploreMode={exploreMode}
          onExploreModeChange={handleExploreModeChange}
          recentlyViewedCount={recentlyViewedCount}
          isAutoScrollPlaying={isAutoScrollPlaying}
          onAutoScrollToggle={toggleAutoScroll}
          filtersOpen={filtersOpen}
          onFiltersOpenChange={setMobileFiltersOpen}
          onFiltersApply={handleFiltersApply}
          activeRegionOverride={activeRegionOverride}
          onCityPillClick={handleCityPillClick}
          onClearRegionOverride={handleClearRegionOverride}
          mobileExpandSection={mobileExpandSection}
          filtersGlowPulseKey={filtersGlowPulseKey}
          mobileHeaderCollapsed={mobileHeaderCollapsed}
          hideEventFilterButton={true}
          filtersReturnFocusRef={discoveryFiltersTriggerRef}
        />
        <EventsLoadingShell />
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Helmet>
        <title>{seoMeta.title}</title>
        <meta name="description" content={seoMeta.description} />
        <meta name="robots" content={seoMeta.robots} />
        <link rel="canonical" href={seoMeta.canonical} />
        <meta property="og:title" content={seoMeta.title} />
        <meta property="og:description" content={seoMeta.description} />
        <meta property="og:url" content={seoMeta.canonical} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="On A Lark" />
        <meta property="og:locale" content="en_US" />
        <meta property="og:image" content={seoMeta.image ?? SHARE_IMAGE_URL} />
        <meta property="og:image:alt" content={seoMeta.title} />
        <meta name="twitter:title" content={seoMeta.title} />
        <meta name="twitter:description" content={seoMeta.description} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content={seoMeta.image ?? SHARE_IMAGE_URL} />
        <meta name="twitter:image:alt" content={seoMeta.title} />
        {landingStructuredData.map((schema, index) => (
          <script key={`home-structured-${index}`} type="application/ld+json">
            {JSON.stringify(schema)}
          </script>
        ))}
      </Helmet>
      <div className="flex flex-col lg:flex-row flex-1 min-h-0 w-full">
      {/* Right column: full width now that the persistent sidebar is replaced by a drawer */}
      <div className="flex-1 min-w-0 flex flex-col">
      {/* Desktop slide-in filter drawer — hidden on mobile, fixed position below header */}
      <FiltersDialog
        open={filtersOpen || desktopDrawerOpen}
        categories={masterCategoryNames}
        sources={sources}
        lockedRegions={routeRegionName ? [routeRegionName] : []}
        initialDraft={dialogFilterDraft}
        onApply={applyDialogFilterDraft}
        onCancel={closeFiltersDialog}
      />
        <Header 
          categories={masterCategoryNames}
          categoryFilters={categoryFilters}
          sources={sources}
          selectedCategories={selectedCategories}
          excludedCategories={excludedCategories}
          selectedSources={selectedSources}
          excludedSources={excludedSources}
          selectedRegions={selectedRegions}
          excludedRegions={excludedRegions}
          searchValue={searchQuery}
          onSearch={handleSearch} 
          onFilterChange={handleFilterChange}
          onExcludeFilterChange={handleExcludeFilterChange}
          onSourceFilterChange={handleSourceFilterChange}
          onExcludeSourceToggle={handleExcludeSourceChange}
          onBulkExcludeSourcesChange={handleBulkExcludeSourcesChange}
          onRegionFilterChange={handleRegionFilterChange}
          onExcludeRegionToggle={sidebarToggleExcludedRegion}
          onDateFilterChange={handleDateFilterChange}
          timePeriods={timePeriods}
          onTimePeriodChange={handleTimePeriodChange}
          dateFilter={dateFilter}
          customDate={customDate}
          onSurpriseMe={handleSurpriseMe}
          surpriseMeDisabled={eventsLoading || filterEventsWithin7Days(displayEvents).length === 0 || activeFilterCount > 0}
          viewMode={viewMode}
          exploreMode={exploreMode}
          onExploreModeChange={handleExploreModeChange}
          recentlyViewedCount={recentlyViewedCount}
          isAutoScrollPlaying={isAutoScrollPlaying}
          onAutoScrollToggle={toggleAutoScroll}
          filtersOpen={false}
          onFiltersOpenChange={setMobileFiltersOpen}
          onFiltersApply={handleFiltersApply}
          desktopFiltersOpen={false}
          onDesktopFiltersToggle={toggleDesktopDrawer}
          activeRegionOverride={activeRegionOverride}
          onCityPillClick={handleCityPillClick}
          onClearRegionOverride={handleClearRegionOverride}
          mobileExpandSection={mobileExpandSection}
          hideNavigationLayer={true}
          filtersGlowPulseKey={filtersGlowPulseKey}
          mobileHeaderCollapsed={mobileHeaderCollapsed}
          hideEventFilterButton={true}
          filtersReturnFocusRef={discoveryFiltersTriggerRef}
        />
        {/* Aria-live region for announcements */}
        <div className="sr-only" role="status" aria-live="polite" aria-atomic="true">
          {viewMode === 'SURPRISE' && 'Showing a random event'}
          {browseAnnouncement}
        </div>
        <UnifiedDiscoveryBar
          heading={landingHeading}
          intro={landingIntro}
          sourceCount={sources.length}
          resultCount={statusBarCount ?? null}
          resultCountLoading={isStatusCountLoading}
          filtersOpen={filtersOpen || desktopDrawerOpen}
          activeQuickDate={activeQuickDate}
          activeFilterCount={discoveryActiveFilterCount}
          chips={discoveryFilterChips}
          exploreMode={exploreMode}
          recentlyViewedCount={recentlyViewedCount}
          filterButtonRef={discoveryFiltersTriggerRef}
          onQuickDateChange={handleQuickDateChange}
          onOpenFilters={openUnifiedFilters}
          onClearAll={() => {
            clearSearch();
            clearFilters();
          }}
          onExploreModeChange={handleExploreModeChange}
          onOpenGuide={handleGuideButton}
          activeGuide={guidePanel}
          regionShortcuts={regionShortcuts}
          onRegionShortcut={handleRegionShortcut}
          mobileFilterSummary={hasActiveFilters && viewMode === 'HOME' && exploreMode === 'events' && !filtersOpen && (() => {
          const mobileSummaryFilterCount = [
            hasIncludeCategories || hasExcludeCategories,
            hasSourceFilters || hasExcludeSourceFilters,
            hasRegionFilters || hasExcludeRegionFilters || !!activeRegionOverride,
            hasDateFilter,
            hasTimeFilter && activeQuickDate !== 'tonight',
            !!searchQuery,
          ].filter(Boolean).length;
          return (
            <div
              role="button"
              tabIndex={0}
              className="w-full cursor-pointer"
              style={{
                padding: '6px 12px 8px',
                background: '#f0f7f3',
              }}
              data-testid="mobile-filter-summary"
              onClick={() => setMobileFiltersOpen(true)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setMobileFiltersOpen(true);
                }
              }}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-1.5 min-w-0">
                  <SlidersHorizontal style={{ width: 13, height: 13, flexShrink: 0, color: '#1D9E75' }} />
                  <span style={{ fontSize: 13, fontWeight: 600, color: '#1a3d2e', whiteSpace: 'nowrap' }}>
                    {mobileSummaryFilterCount > 0
                      ? `${mobileSummaryFilterCount} ${mobileSummaryFilterCount === 1 ? 'filter' : 'filters'} active`
                      : 'Filters active'}
                  </span>
                  <span style={{ fontSize: 13, color: '#7a7468', whiteSpace: 'nowrap' }}>
                    · {statusBarCountText} events
                  </span>
                </div>
                <button
                  type="button"
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: '#c0392b',
                    whiteSpace: 'nowrap',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    padding: '2px 0',
                    flexShrink: 0,
                  }}
                  onClick={(e) => { e.stopPropagation(); clearSearch(); clearFilters(); }}
                  aria-label="Clear all filters"
                  data-testid="mobile-filter-summary-clear"
                >
                  <X style={{ width: 11, height: 11 }} />
                  Clear
                </button>
              </div>
              <div style={{ fontSize: 11, color: '#9e9890', marginTop: 2 }}>
                Tap to edit filters →
              </div>
            </div>
          );
        })()}
        />
        {/* Legacy discovery shell retained temporarily for migration safety, but no longer rendered. */}
        {renderLegacyDiscovery && (
        <div
          className={`w-full ${stickyDiscoveryStrip ? 'sticky z-40' : ''}`}
          style={stickyDiscoveryStrip ? { top: HERO_STICKY_TOP, background: 'rgb(255,255,248)', borderBottom: '0.5px solid #e8e3d7' } : undefined}
        >
          {showExploreModeTabs && (
            <div
              className={`w-full px-3 md:px-4 lg:px-4 ${stickyDiscoveryStrip ? 'pt-1 pb-1' : 'pt-4'}`}
            >
              {showEventsDiscoveryHero && (
                <>
                  <div>
                    <HomeLandingIntro
                      heading={landingHeading}
                      intro={landingIntro}
                      regionPills={heroRegionPills}
                      categoryPills={heroCategoryPills}
                      onRegionClick={routeRegionName || routeCategoryName ? handleHeroRegionNavigation : handleCityPillClick}
                      showAllRegions={!!routeRegionName}
                      onAllRegionsClick={routeRegionName ? handleAllRegionsNavigation : undefined}
                      onCategoryClick={handleHeroCategoryPillClick}
                      mobileResultCount={statusBarCount ?? null}
                      onMobileRegionSelect={handleMobileBrowseRegionSelect}
                      onMobileCategorySelect={handleMobileBrowseCategorySelect}
                      collapsed={heroCollapsed}
                      onCollapse={handleHeroCollapse}
                      onExpand={handleHeroExpand}
                    />
                  </div>
                  {stickyDiscoveryStrip ? (
                    <div
                      className="overflow-hidden rounded-[16px] border-[1.5px] border-[#d9d0c4] bg-white shadow-[0_1px_3px_rgba(0,0,0,0.04)]"
                      data-testid="homepage-discovery-panel"
                    >
                      <div className="px-3 py-2.5 md:px-4 md:py-3">
                        <ExploreModeTabs
                          exploreMode={exploreMode}
                          recentlyViewedCount={recentlyViewedCount}
                          onExploreModeChange={handleExploreModeChange}
                        />
                      </div>
                      <div className="border-t border-[#ece4d7] px-3 py-2 md:px-4 md:py-3">
                        <div className="hidden sm:flex sm:items-center sm:gap-2">
                          <QuickPicksSection
                            guidePanel={guidePanel}
                            onGuideButton={handleGuideButton}
                            className="sm:flex-1 sm:items-center sm:gap-2 sm:space-y-0"
                            buttonsClassName="sm:flex-nowrap"
                            headingClassName="sm:hidden"
                            weekTestId="button-guide-week"
                            weekendTestId="button-guide-weekend"
                          />
                          <button
                            type="button"
                            onClick={handleHeroExpand}
                            className="inline-flex min-h-[44px] w-full items-center justify-between gap-2 rounded-[12px] border border-[#d8e6db] bg-[#f4f8f3] px-3 py-2 text-[13px] font-medium text-[#587765] transition-colors hover:border-[#c9dbce] hover:bg-[#eef5ef] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1D9E75] focus-visible:ring-offset-2 md:w-auto md:justify-start md:px-3.5"
                            data-testid="homepage-discovery-hero-browse"
                          >
                            Browse regions &amp; categories
                            <ChevronDown className="h-4 w-4 shrink-0" />
                          </button>
                        </div>
                        <div className="sm:hidden">
                          <QuickPicksSection
                            guidePanel={guidePanel}
                            onGuideButton={handleGuideButton}
                            weekTestId="button-guide-week"
                            weekendTestId="button-guide-weekend"
                          />
                        </div>
                      </div>
                      {guidePanel && (
                        <div className="hidden border-t border-[#ece4d7] px-3 py-3 md:block md:px-4">
                          <Suspense fallback={null}>
                            <GuidePanel
                              open={guidePanel}
                              onClose={() => setGuidePanel(null)}
                              variant="inline"
                              onOpenEvent={handleOpenGuidePanelEvent}
                            />
                          </Suspense>
                        </div>
                      )}
                      <div className="border-t border-[#ece4d7] px-3 py-2 md:px-4 md:py-3">
                        <EventsDateBar
                          dateFilter={dateFilter}
                          customDate={customDate}
                          onDateFilterChange={handleDateFilterChange}
                          embedded={true}
                          filterQueryString={countsFilterQueryString}
                        />
                      </div>
                      {showSummaryContextLine && (
                        <div className="border-t border-[#ece4d7] px-3 py-2.5 md:px-4">
                          <div className="flex flex-wrap items-center gap-x-2 gap-y-2 text-[13px] text-[#7f776d]">
                            <span className={`font-medium text-[#5f5a52]${statusCountFlashing ? ' count-flash' : ''}`} data-testid="search-status-line">
                              {statusBarCountText} events
                            </span>
                            <span className="text-[#cdbfaa]">|</span>
                            {searchQuery && (
                              <>
                                <span
                                  className="inline-flex items-center gap-1 rounded-[8px] bg-[#f5f1ea] px-2.5 py-1 text-[13px] text-[#6e675d]"
                                  data-testid="status-search-chip"
                                >
                                  <Search className="h-3.5 w-3.5" />
                                  <span>{searchQuery}</span>
                                  <button
                                    type="button"
                                    onClick={clearSearch}
                                    className="inline-flex items-center justify-center text-[#9a9185] transition-colors hover:text-[#5f5a52]"
                                    aria-label="Clear search filter"
                                    data-testid="status-search-label"
                                  >
                                    <X className="h-3.5 w-3.5" />
                                  </button>
                                </span>
                                <span className="text-[#cdbfaa]">|</span>
                              </>
                            )}
                            {dateLabel && (
                              <>
                                <span
                                  className="inline-flex items-center gap-1 rounded-[8px] bg-[#f5f1ea] px-2.5 py-1 text-[13px] text-[#6e675d]"
                                  data-testid="status-date-chip"
                                >
                                  <Calendar className="h-3.5 w-3.5" />
                                  <span>{dateLabel}</span>
                                  <button
                                    type="button"
                                    onClick={() => setDateFilter(null)}
                                    className="inline-flex items-center justify-center text-[#9a9185] transition-colors hover:text-[#5f5a52]"
                                    aria-label="Clear date filter"
                                    data-testid="status-date-label"
                                  >
                                    <X className="h-3.5 w-3.5" />
                                  </button>
                                </span>
                                <span className="text-[#cdbfaa]">|</span>
                              </>
                            )}
                            {timePeriodLabels.map(({ key, label }) => (
                              <span key={key} className="contents">
                                <span
                                  className="inline-flex items-center gap-1 rounded-[8px] bg-[#f5f1ea] px-2.5 py-1 text-[13px] text-[#6e675d]"
                                  data-testid={`status-time-chip-${key}`}
                                >
                                  <Clock className="h-3.5 w-3.5" />
                                  <span>{label}</span>
                                  <button
                                    type="button"
                                    onClick={() => setTimePeriods((current) => {
                                      const remaining = current.filter(period => period !== key);
                                      return remaining.length > 0 ? remaining : ['allDay'];
                                    })}
                                    className="inline-flex items-center justify-center text-[#9a9185] transition-colors hover:text-[#5f5a52]"
                                    aria-label={`Clear ${label.toLowerCase()} time filter`}
                                    data-testid={`status-time-${key}`}
                                  >
                                    <X className="h-3.5 w-3.5" />
                                  </button>
                                </span>
                                <span className="text-[#cdbfaa]">|</span>
                              </span>
                            ))}
                            {activeRegionOverride && (
                              <>
                                <span
                                  className="inline-flex items-center rounded-[8px] bg-[#f5f1ea] px-2.5 py-1 text-[13px] text-[#6e675d]"
                                  data-testid="status-region-override-chip"
                                >
                                  <button
                                    type="button"
                                    className="inline-flex items-center"
                                    onClick={() => openFilterSection('regions')}
                                    data-testid="status-region-override-label"
                                  >
                                    {activeRegionOverride} only
                                  </button>
                                  <button
                                    type="button"
                                    onClick={handleClearRegionOverride}
                                    className="ml-1 inline-flex items-center justify-center text-[#9a9185] transition-colors hover:text-[#5f5a52]"
                                    aria-label="Clear region filter"
                                    data-testid="status-region-override-clear"
                                  >
                                    <X className="h-3.5 w-3.5" />
                                  </button>
                                </span>
                                <span className="text-[#cdbfaa]">|</span>
                              </>
                            )}
                            {!activeRegionOverride && excludedRegions.length > 0 && (
                              <>
                                <span className="inline-flex items-center rounded-[8px] bg-[#f5f1ea] px-2.5 py-1 text-[13px] text-[#6e675d]">
                                  <button
                                    type="button"
                                    className="inline-flex items-center"
                                    onClick={() => openFilterSection('regions')}
                                    data-testid="hiding-regions-trigger"
                                  >
                                    {allRegionNames.length - excludedRegions.length} of {allRegionNames.length} {allRegionNames.length === 1 ? 'region' : 'regions'}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={handleClearRegionOverride}
                                    className="ml-1 inline-flex items-center justify-center text-[#9a9185] transition-colors hover:text-[#5f5a52]"
                                    aria-label="Clear region filters"
                                  >
                                    <X className="h-3.5 w-3.5" />
                                  </button>
                                </span>
                                <span className="text-[#cdbfaa]">|</span>
                              </>
                            )}
                            {excludedCategories.length > 0 && (
                              <>
                                <span className="inline-flex items-center rounded-[8px] bg-[#f5f1ea] px-2.5 py-1 text-[13px] text-[#6e675d]">
                                  <button
                                    type="button"
                                    className="inline-flex items-center"
                                    onClick={() => openFilterSection('categories')}
                                    data-testid="hiding-categories-trigger"
                                  >
                                    {masterCategoryNames.length - excludedCategories.length} of {masterCategoryNames.length} {masterCategoryNames.length === 1 ? 'category' : 'categories'}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={clearCategorySummaryFilters}
                                    className="ml-1 inline-flex items-center justify-center text-[#9a9185] transition-colors hover:text-[#5f5a52]"
                                    aria-label="Clear category filters"
                                  >
                                    <X className="h-3.5 w-3.5" />
                                  </button>
                                </span>
                                <span className="text-[#cdbfaa]">|</span>
                              </>
                            )}
                            {excludedSources.length > 0 && (
                              <>
                                <span className="inline-flex items-center rounded-[8px] bg-[#f5f1ea] px-2.5 py-1 text-[13px] text-[#6e675d]">
                                  <button
                                    type="button"
                                    className="inline-flex items-center"
                                    onClick={() => openFilterSection('sources')}
                                    data-testid="hiding-sources-trigger"
                                  >
                                    {sources.length - excludedSources.length} of {sources.length} {sources.length === 1 ? 'source' : 'sources'}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={clearSourceSummaryFilters}
                                    className="ml-1 inline-flex items-center justify-center text-[#9a9185] transition-colors hover:text-[#5f5a52]"
                                    aria-label="Clear source filters"
                                  >
                                    <X className="h-3.5 w-3.5" />
                                  </button>
                                </span>
                                <span className="text-[#cdbfaa]">|</span>
                              </>
                            )}
                            <button
                              type="button"
                              onClick={() => {
                                clearSearch();
                                clearFilters();
                              }}
                              className="ml-auto text-[13px] text-[#8a8176] underline underline-offset-2 transition-colors hover:text-[#5f5a52]"
                              data-testid="button-clear-all"
                            >
                              Clear all
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <>
                      <div className="px-3 pt-2 md:px-4">
                        <QuickPicksSection
                          guidePanel={guidePanel}
                          onGuideButton={handleGuideButton}
                          weekTestId="button-guide-week-standalone"
                          weekendTestId="button-guide-weekend-standalone"
                        />
                      </div>
                      {guidePanel && (
                        <div className="hidden px-3 pt-3 md:block md:px-4">
                          <Suspense fallback={null}>
                            <GuidePanel
                              open={guidePanel}
                              onClose={() => setGuidePanel(null)}
                              variant="inline"
                              onOpenEvent={handleOpenGuidePanelEvent}
                            />
                          </Suspense>
                        </div>
                      )}
                      <EventsDateBar
                        dateFilter={dateFilter}
                        customDate={customDate}
                        onDateFilterChange={handleDateFilterChange}
                        filterQueryString={countsFilterQueryString}
                      />
                    </>
                  )}
                </>
              )}
              {!stickyDiscoveryStrip && (
                <ExploreModeTabs
                  exploreMode={exploreMode}
                  recentlyViewedCount={recentlyViewedCount}
                  onExploreModeChange={handleExploreModeChange}
                />
              )}
            </div>
          )}
          {/* Filter banner — no sticky class; stacks naturally inside the single sticky shell */}
          {(() => {
        // Calculate if non-text filters are active
        const hasIncludeCategories = selectedCategories.length > 0;
        const hasExcludeCategories = excludedCategories.length > 0;
        const hasSourceFilters = selectedSources.length > 0;
        const hasExcludeSourceFilters = excludedSources.length > 0;
        const hasRegionFilters = selectedRegions.length > 0;
        const hasExcludeRegionFilters = excludedRegions.length > 0;
        const hasDateFilter = dateFilter !== null;
        const hasTimeFilter = !(timePeriods.length === 1 && timePeriods[0] === 'allDay');
        const hasNonTextFilters = hasIncludeCategories || hasExcludeCategories || hasSourceFilters || hasExcludeSourceFilters || hasRegionFilters || hasExcludeRegionFilters || hasDateFilter || hasTimeFilter;
        
        // Show banner only on the Events tab — hide on Sources/Recently Viewed without clearing state
        const showBanner = (searchQuery || hasNonTextFilters) && viewMode === 'HOME' && !filtersOpen && exploreMode === 'events';
        
        if (!showBanner || stickyDiscoveryStrip) return null;
        
        // Final visible count - use server total when not searching (server-side filtering)
        // For search results, use displayEvents.length (client-side filtering)
        const finalCount = searchQuery ? displayEvents.length : (serverFilteredTotal ?? 0);
        
        // Build date/time label for Showing line
        const getDateLabel = (): string | null => {
          if (!dateFilter) return null;
          switch (dateFilter) {
            case 'today': return 'Today';
            case 'tomorrow': return 'Tomorrow';
            case 'weekend': return 'This Weekend';
            case 'custom': return customDate ? new Date(customDate + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : null;
            default: return null;
          }
        };
        
        const getTimeLabel = (): string | null => {
          if (timePeriods.includes('allDay')) return null;
          const labels = timePeriods.map(p => {
            switch (p) {
              case 'morning': return 'Morning';
              case 'afternoon': return 'Afternoon';
              case 'evening': return 'Evening';
              default: return null;
            }
          }).filter(Boolean);
          return labels.length > 0 ? labels.join(' · ') : null;
        };
        
        const dateLabel = getDateLabel();
        const timeLabel = getTimeLabel();
        
        // Helper to remove a single filter
        const removeDateFilter = () => {
          setDateFilter(null);
          trackCommittedFilterUsage({
            dateFilter: null,
            customDate: null,
          });
        };
        const removeTimePeriod = (period: TimePeriod) => {
          const remaining = timePeriods.filter(p => p !== period);
          setTimePeriods(remaining.length > 0 ? remaining : ['allDay']);
          trackCommittedFilterUsage({
            timePeriods: remaining.length > 0 ? remaining : ['allDay'],
          });
        };
        
        return (
          <div ref={searchBannerRef}>
            {/* Desktop: existing full results bar */}
            <div
              className={`hidden md:block search-results-banner w-full px-4 lg:px-6 bg-background pt-2 pb-2.5${!stickyDiscoveryStrip ? ' sticky z-40' : ''}`}
              style={!stickyDiscoveryStrip ? { top: HERO_STICKY_TOP } : { paddingTop: 2, paddingBottom: 3 }}
              data-testid="search-results-banner"
            >
            <div className={`px-0 ${stickyDiscoveryStrip ? 'py-0.5' : 'py-1.5'}`}>
              <div className={`flex items-center justify-start flex-wrap ${stickyDiscoveryStrip ? 'gap-1.5' : 'gap-2'}`}>
                <p className={`${stickyDiscoveryStrip ? 'text-[10px]' : 'text-[14px]'} font-bold text-muted-foreground shrink-0${statusCountFlashing ? ' count-flash' : ''}`} data-testid="search-status-line">
                  Showing {statusBarCountText} events
                </p>

                <div className={`flex items-center flex-wrap justify-start ${stickyDiscoveryStrip ? 'gap-1' : 'gap-1.5'}`}>
                  {searchQuery && (
                    searchLoading ? (
                      <span
                        className={`inline-flex items-center rounded-sm border border-border/50 bg-muted/40 text-muted-foreground font-medium ${stickyDiscoveryStrip ? 'gap-1 text-[10px] px-2 py-0.5' : 'gap-1.5 text-[15px] px-3 py-1'}`}
                        data-testid="status-search-chip-loading"
                      >
                        <span className={`${stickyDiscoveryStrip ? 'h-2.5 w-2.5' : 'h-3.5 w-3.5'} animate-spin rounded-full border-2 border-muted-foreground border-t-transparent`} />
                        <span>searching...</span>
                      </span>
                    ) : (
                      <div
                        className="inline-flex items-center rounded-sm border border-border/50 bg-muted/40 overflow-hidden cursor-pointer hover:border-border transition-colors"
                        data-testid="status-search-chip"
                      >
                        <span className={`inline-flex items-center text-muted-foreground font-medium ${stickyDiscoveryStrip ? 'gap-1 text-[10px] px-2 py-0.5' : 'gap-1.5 text-[15px] px-3 py-1'}`}>
                          <Search className={stickyDiscoveryStrip ? 'h-3 w-3' : 'h-3.5 w-3.5'} />
                          <span>{searchQuery}</span>
                        </span>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              type="button"
                              onClick={clearSearch}
                              className={`inline-flex items-center justify-center text-foreground/50 hover:text-foreground hover:bg-black/10 dark:hover:bg-white/15 transition-colors border-l border-foreground/20 cursor-pointer ${stickyDiscoveryStrip ? 'w-5 h-5' : 'w-6 h-6'}`}
                              aria-label="Clear search filter"
                              data-testid="status-search-label"
                            >
                              <X className={stickyDiscoveryStrip ? 'h-3 w-3' : 'h-4 w-4'} />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent>Clear all search filters</TooltipContent>
                        </Tooltip>
                      </div>
                    )
                  )}

                  {dateLabel && (
                    <div
                      className="inline-flex items-center rounded-sm border border-border/50 bg-muted/40 overflow-hidden cursor-pointer hover:border-border transition-colors"
                      data-testid="status-date-chip"
                    >
                      <span className={`inline-flex items-center text-muted-foreground font-medium ${stickyDiscoveryStrip ? 'gap-1 text-[10px] px-2 py-0.5' : 'gap-1.5 text-[15px] px-3 py-1'}`}>
                        <Calendar className={stickyDiscoveryStrip ? 'h-3 w-3' : 'h-3.5 w-3.5'} />
                        <span>{dateLabel}</span>
                      </span>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            onClick={removeDateFilter}
                            className={`inline-flex items-center justify-center text-foreground/50 hover:text-foreground hover:bg-black/10 dark:hover:bg-white/15 transition-colors border-l border-foreground/20 cursor-pointer ${stickyDiscoveryStrip ? 'w-5 h-5' : 'w-6 h-6'}`}
                            aria-label="Clear date filter"
                            data-testid="status-date-label"
                          >
                            <X className={stickyDiscoveryStrip ? 'h-3 w-3' : 'h-4 w-4'} />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent>Clear all date filters</TooltipContent>
                      </Tooltip>
                    </div>
                  )}

                  {timePeriods.filter(p => p !== 'allDay').map(period => (
                    <div
                      key={period}
                      className="inline-flex items-center rounded-sm border border-border/50 bg-muted/40 overflow-hidden cursor-pointer hover:border-border transition-colors"
                      data-testid={`status-time-chip-${period}`}
                    >
                      <span className={`inline-flex items-center text-muted-foreground font-medium ${stickyDiscoveryStrip ? 'gap-1 text-[10px] px-2 py-0.5' : 'gap-1.5 text-[15px] px-3 py-1'}`}>
                        <Clock className={stickyDiscoveryStrip ? 'h-3 w-3' : 'h-3.5 w-3.5'} />
                        <span>{period === 'morning' ? 'Morning' : period === 'afternoon' ? 'Afternoon' : 'Evening'}</span>
                      </span>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            onClick={() => removeTimePeriod(period)}
                            className={`inline-flex items-center justify-center text-foreground/50 hover:text-foreground hover:bg-black/10 dark:hover:bg-white/15 transition-colors border-l border-foreground/20 cursor-pointer ${stickyDiscoveryStrip ? 'w-5 h-5' : 'w-6 h-6'}`}
                            aria-label={`Clear ${period} time filter`}
                            data-testid={`status-time-${period}`}
                          >
                            <X className={stickyDiscoveryStrip ? 'h-3 w-3' : 'h-4 w-4'} />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent>Clear all {period === 'morning' ? 'morning' : period === 'afternoon' ? 'afternoon' : 'evening'} time filters</TooltipContent>
                      </Tooltip>
                    </div>
                  ))}

                  {activeRegionOverride && (
                    <div
                      className="inline-flex items-center rounded-sm border border-border/50 bg-muted/40 overflow-hidden cursor-pointer hover:border-border transition-colors"
                      data-testid="status-region-override-chip"
                    >
                      <button
                        type="button"
                        className={`inline-flex items-center text-muted-foreground font-medium ${stickyDiscoveryStrip ? 'gap-1 text-[10px] px-2 py-0.5' : 'gap-1.5 text-[15px] px-3 py-1'}`}
                        onClick={() => {
                          openDesktopDrawer();
                          setSidebarExpandSection({ section: 'regions', ts: Date.now() });
                        }}
                        data-testid="status-region-override-label"
                      >
                        {activeRegionOverride} only
                          <ChevronDown className={stickyDiscoveryStrip ? 'h-3 w-3' : 'h-3.5 w-3.5'} />
                      </button>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            onClick={handleClearRegionOverride}
                            className={`inline-flex items-center justify-center text-foreground/50 hover:text-foreground hover:bg-black/10 dark:hover:bg-white/15 transition-colors border-l border-foreground/20 cursor-pointer ${stickyDiscoveryStrip ? 'w-5 h-5' : 'w-6 h-6'}`}
                            aria-label="Clear region override"
                            data-testid="status-region-override-clear"
                          >
                            <X className={stickyDiscoveryStrip ? 'h-3 w-3' : 'h-4 w-4'} />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent>Clear region filter</TooltipContent>
                      </Tooltip>
                    </div>
                  )}

                  {!activeRegionOverride && excludedRegions.length > 0 && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          className={`inline-flex items-center font-medium transition-opacity hover:opacity-80 cursor-pointer ${stickyDiscoveryStrip ? 'gap-1 text-[10px] px-2 py-0.5' : 'gap-1.5 text-[11px] px-2.5 py-1'}`}
                          style={{ background: '#E1F5EE', border: '0.5px solid #9FE1CB', color: '#085041', borderRadius: 99 }}
                          onClick={() => {
                            openDesktopDrawer();
                            setSidebarExpandSection({ section: 'regions', ts: Date.now() });
                          }}
                          data-testid="hiding-regions-trigger"
                        >
                          {allRegionNames.length - excludedRegions.length} of {allRegionNames.length} {allRegionNames.length === 1 ? 'region' : 'regions'}
                          <ChevronDown className="h-3 w-3" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent>Open Filters to manage excluded regions</TooltipContent>
                    </Tooltip>
                  )}

                  {excludedCategories.length > 0 && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          className={`inline-flex items-center font-medium transition-opacity hover:opacity-80 cursor-pointer ${stickyDiscoveryStrip ? 'gap-1 text-[10px] px-2 py-0.5' : 'gap-1.5 text-[11px] px-2.5 py-1'}`}
                          style={{ background: '#E1F5EE', border: '0.5px solid #9FE1CB', color: '#085041', borderRadius: 99 }}
                          onClick={() => {
                            openDesktopDrawer();
                            setSidebarExpandSection({ section: 'categories', ts: Date.now() });
                          }}
                          data-testid="hiding-categories-trigger"
                        >
                          {masterCategoryNames.length - excludedCategories.length} of {masterCategoryNames.length} {masterCategoryNames.length === 1 ? 'category' : 'categories'}
                          <ChevronDown className="h-3 w-3" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent>Open Filters to manage excluded categories</TooltipContent>
                    </Tooltip>
                  )}

                  {excludedSources.length > 0 && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          className={`inline-flex items-center font-medium transition-opacity hover:opacity-80 cursor-pointer ${stickyDiscoveryStrip ? 'gap-1 text-[10px] px-2 py-0.5' : 'gap-1.5 text-[11px] px-2.5 py-1'}`}
                          style={{ background: '#E1F5EE', border: '0.5px solid #9FE1CB', color: '#085041', borderRadius: 99 }}
                          onClick={() => {
                            openDesktopDrawer();
                            setSidebarExpandSection({ section: 'sources', ts: Date.now() });
                          }}
                          data-testid="hiding-sources-trigger"
                        >
                          {sources.length - excludedSources.length} of {sources.length} {sources.length === 1 ? 'source' : 'sources'}
                          <ChevronDown className="h-3 w-3" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent>Open Filters to manage excluded sources</TooltipContent>
                    </Tooltip>
                  )}

                  {(searchQuery || hasNonTextFilters) && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        clearSearch();
                        clearFilters();
                      }}
                      className={`ml-1 rounded-sm text-muted-foreground/60 hover:text-muted-foreground ${stickyDiscoveryStrip ? 'text-[10px] px-1 py-0.5' : 'text-sm'}`}
                      data-testid="button-clear-all"
                    >
                      Clear filters
                    </Button>
                  )}
                </div>
              </div>
              
            </div>
            </div>
          </div>
        );
      })()}
        </div>
        )}
        {/* Events list */}
        <div className={`flex flex-col transition-opacity duration-200 ${eventsFetching && !isFetchingNextPage ? 'opacity-60' : 'opacity-100'}`}>
      {/* Region override warning bar — shown when a city quick-select is active */}
      {renderLegacyFilterAlerts && activeRegionOverride && exploreMode === 'events' && (
        <div className="hidden md:block w-full px-4 lg:px-6 pt-2" data-testid="region-override-warning-bar">
          <div
            className="flex items-center gap-3"
            style={{ background: '#FFF8E0', border: '1px solid #FAC775', borderRadius: '10px', padding: '10px 16px' }}
          >
            <span className="flex-1 text-sm" style={{ color: '#7A5C00' }}>
              Showing <strong>{activeRegionOverride}</strong> events only. To see all regions, tap{' '}
              <button
                type="button"
                className="font-medium underline underline-offset-2 hover:opacity-80 transition-opacity"
                style={{ color: '#7A5C00' }}
                onClick={handleClearRegionOverride}
                data-testid="button-region-override-clear-warning"
              >
                All regions
              </button>
              {' '}or open Filters to customize.
            </span>
          </div>
        </div>
      )}
      {/* OAL-SOFTEN: change 12 — full-width amber alert strip for excluded regions */}
      {renderLegacyFilterAlerts && !activeRegionOverride && excludedRegions.length > 0 && !regionAlertDismissed && exploreMode === 'events' && (
        <div className="hidden md:block w-full px-4 lg:px-6 pt-2" data-testid="region-hidden-alert">
          <div
            className="flex items-center gap-3"
            style={{ background: '#FFF8E0', border: '1px solid #FAC775', borderRadius: '10px', padding: '10px 16px' }}
          >
            <span className="flex-1 text-sm" style={{ color: '#7A5C00' }}>
              ⚠ {excludedRegions.length} {excludedRegions.length === 1 ? 'region is' : 'regions are'} excluded from your results —{' '}
              <button
                type="button"
                className="font-medium underline underline-offset-2 hover:opacity-80 transition-opacity"
                style={{ color: '#7A5C00' }}
                onClick={() => setExcludedRegions([])}
                data-testid="button-region-alert-show-all"
              >
                Add them back
              </button>
            </span>
            <button
              type="button"
              className="text-muted-foreground hover:text-foreground transition-colors shrink-0 ml-1 text-base leading-none"
              onClick={() => setRegionAlertDismissed(true)}
              aria-label="Dismiss region alert"
              data-testid="button-region-alert-dismiss"
            >
              ×
            </button>
          </div>
        </div>
      )}
      {hasActiveFilters && !bookmarkBannerDismissed && (
        <div
          data-testid="banner-bookmark-hint"
          className="w-full px-4 lg:px-6 py-2"
        >
          <div className="flex items-start gap-3 rounded-md border border-border bg-muted/50 px-3 py-2.5">
            <Bookmark className="h-4 w-4 shrink-0 mt-0.5 text-muted-foreground" />
            <p className="flex-1 text-foreground text-[12px]">
              Found your perfect filter combo? Bookmark this page! The link includes all your filters, so it'll look exactly like this every time you open it. On most phones and computers, you can save it by pressing <span className="font-medium">Ctrl+D</span> (or <span className="font-medium">Cmd+D</span> on Mac).
            </p>
            <button
              type="button"
              data-testid="button-dismiss-bookmark-banner"
              aria-label="Dismiss bookmark hint"
              onClick={() => {
                localStorage.setItem('bookmarkBannerDismissed', 'true');
                setBookmarkBannerDismissed(true);
              }}
              className="shrink-0 text-muted-foreground hover:text-foreground transition-colors"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      )}
      {!filtersOpen && (
      <main id="event-results" tabIndex={-1} aria-label="Event results" className="flex-1 outline-none">
        {viewMode === 'SURPRISE' ? (
          <div className="w-full px-4 lg:px-6 py-12" ref={surpriseCardRef} tabIndex={-1}>
            {randomEvent ? (
              <Suspense fallback={null}>
                <SurpriseEventCard
                  {...randomEvent}
                  masterCategoryName={randomEvent.category ? rawCategoryToMasterData.get(randomEvent.category)?.name : undefined}
                  masterCategoryIcon={randomEvent.category ? rawCategoryToMasterData.get(randomEvent.category)?.icon : undefined}
                  masterCategoryColor={randomEvent.category ? rawCategoryToMasterData.get(randomEvent.category)?.color : undefined}
                  onDoItAgain={handleDoItAgain}
                  onLater={handleLater}
                  onOpenEventDrawer={handleOpenEventDrawer}
                />
              </Suspense>
            ) : (
              <div className="text-center py-12">
                <p className="text-muted-foreground mb-4">No upcoming events match your filters.</p>
                <div className="flex gap-3 justify-center">
                  <Button onClick={clearFilters} data-testid="button-clear-filters">
                    Clear filters
                  </Button>
                  <Button onClick={handleLater} variant="outline" data-testid="button-later">
                    Later
                  </Button>
                </div>
              </div>
            )}
          </div>
        ) : (
        <div className="w-full px-4 pb-6 pt-1 md:pt-6 lg:px-6">
          {/* Less top padding on phones so the first event card sits closer to the date shortcuts */}
          {/* OAL-DORMANT: change 3 — passive inline notice shown when By Source/Recent AND filters active */}
          {showFilterNotice && (
            <div
              className="flex items-center justify-between gap-3 mb-4"
              style={{ background: '#FFF8E0', border: '1px solid #FAC775', borderRadius: 8, padding: '8px 14px', fontSize: 13, color: '#854F0B' }} /* OAL-DORMANT: change 3 */
              data-testid="banner-filter-notice"
            >
              <span>Your filters are paused while you browse this section. They still apply in All events.</span> {/* OAL-DORMANT: change 3 */}
              <button
                type="button"
                onClick={() => setFilterNoticeDismissed(true)} /* OAL-DORMANT: change 3 — manual dismiss only; resets on mode switch */
                className="shrink-0 transition-opacity"
                style={{ color: '#aaa', fontSize: 14, lineHeight: 1 }}
                aria-label="Dismiss notice"
                data-testid="button-dismiss-filter-notice"
              >
                ×
              </button>
            </div>
          )}
          {exploreMode === 'sources' ? (
            <Suspense fallback={
              <div className="space-y-4">
                <div className="h-5 bg-muted animate-pulse rounded-sm w-56" />
                <div className="h-10 bg-muted animate-pulse rounded-sm" />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[...Array(6)].map((_, i) => (
                    <div key={i} className="rounded-sm p-4 space-y-3 border-2" style={{ borderColor: 'var(--brutal-border-color)' }}>
                      <div className="h-4 bg-muted animate-pulse rounded-sm w-3/4" />
                      <div className="h-8 bg-muted animate-pulse rounded-sm w-1/3" />
                      <div className="h-9 bg-muted animate-pulse rounded-sm" />
                    </div>
                  ))}
                </div>
              </div>
            }>
              <SourceCardsGrid onViewEvents={() => handleExploreModeChange('events')} />
            </Suspense>
          ) : exploreMode === 'recently-viewed' ? (
            <div data-testid="recently-viewed-section">
              {recentlyViewedEvents.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20 gap-4 text-center max-w-xs mx-auto" data-testid="recently-viewed-empty">
                  <Clock className="h-10 w-10 text-muted-foreground/40" />
                  <div className="space-y-1.5">
                    <p className="text-base font-semibold text-foreground">Nothing here yet</p>
                    <p className="text-sm text-muted-foreground">Check back after you've viewed some events and you'll see them here.</p>
                  </div>
                  <p className="text-xs text-muted-foreground/60">Only visible to you · automatically cleared after 90 days</p>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between gap-4 mb-4 bg-[#7d7d7d4d] font-semibold">
                    <span className="text-muted-foreground ml-[0px] mr-[0px] pl-[0px] pr-[0px] pt-[2px] pb-[2px] mt-[0px] mb-[0px] text-center text-[14px]"> This list is only visible to you and is automatically cleared after 90 days.</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="shrink-0 text-xs text-muted-foreground gap-1.5"
                      onClick={() => clearRecentlyViewedMutation.mutate()}
                      disabled={clearRecentlyViewedMutation.isPending}
                      data-testid="button-clear-recently-viewed"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                      {clearRecentlyViewedMutation.isPending ? 'Clearing...' : 'Clear history'}
                    </Button>
                  </div>
                  <div className="mobile-event-card-list grid gap-4 py-4" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))' }}>
                    <Suspense fallback={<EventCardGridShell count={6} />}>
                      {recentlyViewedEvents.map((event, idx) => {
                        const masterData = event.category ? rawCategoryToMasterData.get(event.category) : undefined;
                        return (
                          <div key={`${event.sourceUrl}-${idx}`} className="w-full flex flex-col [&>div]:flex-1">
                            <CompactEventItem
                              title={event.title ?? ''}
                              venue={event.venue ?? ''}
                              date={event.date ?? ''}
                              time={event.time ?? ''}
                              eventDate={event.eventDate}
                              category={event.category}
                              sourceUrl={event.sourceUrl}
                              source={event.source}
                              masterCategoryName={masterData?.name}
                              masterCategoryIcon={masterData?.icon}
                              masterCategoryColor={masterData?.color}
                              isAdmin={false}
                              isAuthenticated={isAuthenticated}
                              clickContext="recently_viewed"
                              onOpenEventDrawer={handleOpenEventDrawer}
                              userAccountsEnabled={userAccountsEnabled}
                            />
                          </div>
                        );
                      })}
                    </Suspense>
                  </div>
                </>
              )}
            </div>
          ) : (
          <div>
            {!routeRegionName && !searchQuery && (
              <h2 className="sr-only">Events Today in Albany &amp; the Capital District</h2>
            )}

            {!searchQuery && !hasActiveFilters && featuredEvents.length > 0 && (
              <Suspense fallback={<div className="min-h-[280px] animate-pulse rounded-xl border border-border/80 bg-card/70 lg:min-h-[320px]" />}>
                <FeaturedEventsSection featuredCount={featuredEvents.length}>
                  <FeaturedCarousel onInteraction={handleFeaturedInteraction}>
                    {featuredEventCards}
                  </FeaturedCarousel>
                </FeaturedEventsSection>
              </Suspense>
            )}
            
            {searchQuery && searchLoading && (
              <p className="text-sm text-muted-foreground py-4">
                Searching...
              </p>
            )}
            {regularEvents.length === 0 && (!!searchQuery || hasActiveFilters) && !searchLoading && (
              <div
                className="my-3 rounded-lg border border-border bg-muted px-3.5 py-3 text-[13px] text-foreground"
                data-testid="search-mismatch-banner"
              >
                <div className="flex items-center gap-2">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0 text-muted-foreground" aria-hidden="true">
                    <path d="M9 18h6" /><path d="M10 22h4" /><path d="M12 2a7 7 0 0 0-4 12.7V17h8v-2.3A7 7 0 0 0 12 2z" />
                  </svg>
                  <p className="flex-1 font-medium">
                    {searchQuery
                      ? `No events match "${searchQuery}"${hasActiveFilters ? " with your filters" : ""}.`
                      : "No events match these filters."}
                  </p>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => { clearSearch(); clearFilters(); }}
                    className="flex-shrink-0 font-medium underline text-primary"
                    data-testid="link-clear-search-mismatch"
                  >
                    Clear all
                  </Button>
                </div>
                {/* Ways forward instead of a dead end */}
                <div className="mt-2 flex flex-wrap items-center gap-2" data-testid="no-results-suggestions">
                  {searchQuery && hasActiveFilters && (
                    <button
                      type="button"
                      onClick={handleSearchWithoutFilters}
                      className="inline-flex min-h-[44px] items-center rounded-full border border-[#16795e] bg-white px-3 text-xs font-semibold text-[#105b45] hover:bg-[#f1faf6] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1D9E75]"
                      data-testid="no-results-search-all"
                    >
                      Search all events for "{searchQuery}"
                    </button>
                  )}
                  {weekendPicks.length >= 3 && (
                    <button
                      type="button"
                      onClick={() => handleGuideButton("weekend")}
                      aria-haspopup="dialog"
                      className="inline-flex min-h-[44px] items-center rounded-full border border-[#cfe1d5] bg-white px-3 text-xs font-semibold text-[#17664f] hover:border-[#8fbb9f] hover:bg-[#f0f7f2] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1D9E75]"
                      data-testid="no-results-weekend-picks"
                    >
                      See this weekend&apos;s picks
                    </button>
                  )}
                  {masterCategoryNames.length > 0 && (
                    <>
                      <span className="text-xs text-muted-foreground">or browse:</span>
                      {masterCategoryNames.slice(0, 6).map((name) => (
                        <button
                          key={name}
                          type="button"
                          onClick={() => handleCategoryPillClick(name)}
                          className="inline-flex min-h-[44px] items-center rounded-full border border-border bg-white px-3 text-xs font-medium text-foreground hover:bg-[#f6f8f6] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1D9E75]"
                          data-testid={`no-results-category-${name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        >
                          {name}
                        </button>
                      ))}
                    </>
                  )}
                </div>
              </div>
            )}
            
            <div data-testid="event-results-list">
            {loadedDates.length > 0 && (
              <DayPeriodJumpBar
                dates={loadedDates}
                periodsByDate={periodsByDate}
                listInstanceId={listInstanceId}
                hasNextPage={Boolean(!searchQuery && hasNextPage)}
                isFetchingNextPage={isFetchingNextPage}
                isLoadError={isFetchNextPageError}
                onLoadMore={loadMoreForJump}
              />
            )}
            <Suspense fallback={<EventGroupsShell />}>
              <EventGroupsSection
                groupedEvents={groupedEvents}
                rawCategoryToMasterData={rawCategoryToMasterData}
                dateHeaderRefs={dateHeaderRefs}
                isAdmin={isAdmin}
                isAuthenticated={isAuthenticated}
                larkedEventSet={larkedEventSet}
                onToggleFeatured={handleFeaturedEventAdd}
                onDelete={handleEventDelete}
                onToggleLark={handleEventLarkToggle}
                onRegionClick={handleRegionPillClick}
                onSourceClick={handleSourceTextClick}
                onCategoryClick={handleCategoryPillClick}
                onDateClick={handleDateTextClick}
                clickContext={clickContextValue}
                queryTerm={searchQuery}
                filtersJson={filtersJsonValue}
                discoveryCollapsed={heroCollapsed}
                listInstanceId={listInstanceId}
                totalListSize={groupedEvents.reduce((sum, g) => sum + g.events.length, 0)}
                userAccountsEnabled={userAccountsEnabled}
                onOpenEventDrawer={handleOpenEventDrawer}
                guidePickMap={guidePickMap}
                interlude={eventListInterlude}
              />
            </Suspense>
            {/* Infinite scroll sentinel and loading indicator */}
            {!searchQuery && (
              <div ref={loadMoreRef} className="py-8 text-center">
                {isFetchingNextPage && (
                  <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground" data-testid="loading-more">
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                    Loading more events...
                  </div>
                )}
                {hasNextPage && !isFetchingNextPage && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="rounded-sm font-bold border-2 border-[var(--brutal-border-color)] shadow-[var(--brutal-shadow-sm)] transition-all duration-100 active:translate-x-[2px] active:translate-y-[2px] active:shadow-none"
                    onClick={() => fetchNextPage()}
                    data-testid="button-load-more"
                  >
                    Load More Events
                  </Button>
                )}
                {!hasNextPage && regularEvents.length > 0 && listEndNote}
              </div>
            )}
            {/* Search results arrive all at once, so their end needs no sentinel */}
            {searchQuery && !searchLoading && regularEvents.length > 0 && (
              <div className="py-8 text-center">
                {listEndNote}
              </div>
            )}
            <DayPeriodJumpSpacer />
            </div>
          </div>
          )}
        </div>
        )}
      </main>
      )}
      {/* Mobile floating filter FAB — visible only when header is collapsed */}
      {(() => {
        const fabFilterCount = [
          hasIncludeCategories || hasExcludeCategories,
          hasSourceFilters || hasExcludeSourceFilters,
          hasRegionFilters || hasExcludeRegionFilters || !!activeRegionOverride,
          hasDateFilter,
          hasTimeFilter,
          !!searchQuery,
        ].filter(Boolean).length;
        const fabVisible = mobileHeaderCollapsed && viewMode === 'HOME' && exploreMode === 'events';
        return (
          <button
            type="button"
            className="md:hidden fixed z-40 flex items-center justify-center"
            style={{
              bottom: 24,
              right: 20,
              width: 52,
              height: 52,
              borderRadius: '50%',
              background: '#1D9E75',
              border: 'none',
              boxShadow: '0 4px 16px rgba(0,0,0,0.18)',
              cursor: 'pointer',
              opacity: fabVisible ? 1 : 0,
              pointerEvents: fabVisible ? 'auto' : 'none',
              transform: fabVisible ? 'scale(1)' : 'scale(0.8)',
              transition: 'opacity 150ms ease, transform 150ms ease',
            }}
            onClick={() => setMobileFiltersOpen(true)}
            aria-label={fabFilterCount > 0 ? `Open filters, ${fabFilterCount} active` : 'Open filters'}
            data-testid="mobile-filter-fab"
          >
            <SlidersHorizontal style={{ width: 22, height: 22, color: '#fff' }} />
            {fabFilterCount > 0 && (
              <span
                style={{
                  position: 'absolute',
                  top: 6,
                  right: 6,
                  minWidth: 16,
                  height: 16,
                  borderRadius: 99,
                  background: '#F97316',
                  color: '#fff',
                  fontSize: 10,
                  fontWeight: 700,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '0 3px',
                  lineHeight: 1,
                  border: '1.5px solid #fff',
                }}
                aria-hidden="true"
              >
                {fabFilterCount}
              </span>
            )}
          </button>
        );
      })()}
      {!filtersOpen && <Footer />}
      <Suspense fallback={null}>
        <GuidePanel open={guidePanel} onClose={() => setGuidePanel(null)} variant="sheet" onOpenEvent={handleOpenGuidePanelEvent} />
      </Suspense>
      <Suspense fallback={null}>
        <EventDrawer event={selectedDrawerEvent} isOpen={!!selectedDrawerEvent} onClose={handleCloseEventDrawer} />
      </Suspense>
        </div>
      </div>
      </div>
    </div>
  );
}
